// @ts-nocheck
// client/src/v2/caderno-folha.tsx
// O CADERNO PESSOAL COMO CADERNO — abrir é já estar escrevendo.
//
// EXCLUSIVO do caderno pessoal. O caderno de um livro continua sendo a página
// de estudos dele, com espinha, âncora de página e o compositor de sempre.
// Aqui não há livro, não há página do livro, não há espinha: há papel.
//
// ── A PÁGINA É VITRINE, NÃO GUARDA ──────────────────────────────────────
//
// Esta é a regra da casa, e ela nasceu de um estrago.
//
// Até 30/09/2026 cada folha era uma LINHA de `book_notes`, e a quebra de
// página era GRAVADA: para mostrar o caderno noutro formato, o app cortava o
// texto e salvava o corte. Quando um parágrafo sozinho era maior que a folha,
// o corte era por palavra e fazia `bloco.textContent = ...` — o que apaga todo
// filho do bloco. Negrito, itálico e letra escolhida morriam ali.
//
// O que isso produzia, medido em laboratório: trocar "folha única" por
// "empilhadas" transformou 1 linha em 3, 5 pedaços em Garamond em 0 e 2 em
// negrito em 0. E não era preciso nem trocar — só ABRIR o caderno num modo
// paginado já limpava a formatação e cortava frases no meio ("...que mais se
// encaixa com" numa folha, "nossa personalidade e natureza." na seguinte).
// O texto sobrevivia; a forma, não. E não havia volta: nada no app juntava as
// folhas de novo, então cada abertura só fragmentava mais.
//
// Agora:
//
//   O CADERNO É UM DOCUMENTO SÓ. Uma linha no banco, um documento na memória.
//   A página existe na TELA e em lugar nenhum mais: é um recorte calculado por
//   medição, e nenhum caminho deste arquivo escreve um recorte de volta.
//   Trocar lado a lado / empilhadas / folha única é redesenhar.
//
// O que se ganha não é o bug consertado: é a categoria inteira do bug
// desaparecendo. Não existe mais código capaz de mexer no texto por causa da
// vitrine, porque a vitrine não tem para onde escrever.
//
// Uma consequência boa: a folha CRESCE se um parágrafo sozinho não couber, em
// vez de ele ser cortado. Papel de verdade não parte palavra ao virar página.
//
// ── A FOLHA 5 SE EDITA ESTANDO NA 20 ────────────────────────────────────
//
// Continua valendo, e agora de graça: é um documento só, e cada página é uma
// janela editável sobre ele. Escrever numa janela recompõe o documento inteiro
// e as janelas seguintes se reacomodam.
//
// ── O TEXTO É FORMATADO, NÃO MARCADO ────────────────────────────────────
//
// O corpo NÃO é HTML. É um documento — lista de blocos com texto puro e
// sinalizadores enumerados, o mesmo modelo do Documentos do Google. O
// contentEditable produz HTML na tela porque é assim que o navegador edita
// texto, mas esse HTML é descartável: a cada tecla o documento é LIDO do DOM e
// vira modelo, e o que foi colado e não cabe no modelo não tem para onde ir.
// O HTML nunca dá a volta — nunca sai do banco para a tela. Ver `doc.ts`.
//
// A folha escrita antes desta mudança está em Markdown e é convertida na hora
// de abrir; o banco só muda quando a pessoa de fato editar. Ninguém tem a nota
// reescrita por ter passado o olho.
//
// ── POR QUE execCommand, QUE É "OBSOLETO" ───────────────────────────────
//
// Porque `forjamente-deploy` roda só `npm run build`, nunca `npm install`:
// dependência nova NÃO chega em produção sozinha. Um editor de verdade
// (Lexical, ProseMirror) entraria no package.json e quebraria calado no
// primeiro deploy. `execCommand` é obsoleto no papel e funciona em todos os
// navegadores que o app alcança, sem instalar nada.
//
// ── O QUE A PESQUISA MUDOU NO DESENHO ───────────────────────────────────
//
// A COR: o creme "de referência" (#FFFDD0) é amarelo demais em tela — vira
// nota-adesiva. Fundo branco puro cansa em sessão longa. Daí #F6F0E2 com tinta
// #2A2520: couché amarelado, não manteiga.

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { api } from '../services/apiV2';
import { Icon } from './icons';
import { deDom, paraDom, deHtml, paraDocumento, paraTexto, validar, PARA_TAMANHO, MAX_TEXTO, MAX_BLOCOS } from './doc';
import { procurar, realcar, limparRealce, irPara, CLASSE_REALCE } from './procurar';


// ── O PAPEL ────────────────────────────────────────────────────────────
// Cores literais aqui são CORRETAS e a única exceção do arquivo: a folha é
// papel, não interface. Ela não segue as 4 paletas justamente porque a ideia
// é sair do app e entrar no caderno.
const PAPEL = {
  fundo: '#F6F0E2',
  tinta: '#2A2520',
  fraco: '#8A8072',
  linha: '#E4DBC6',
  margem: '#D9B7A0',
};

// O respiro do papel, em números e não em string: a conta da página precisa
// saber quanto sobra para o texto, e uma `padding: '24px 20px 24px 30px'`
// escondida num objeto de estilo não se lê de volta.
const RESPIRO = { topo: 24, baixo: 24, esquerda: 30, direita: 20 };

// ── A COLEÇÃO DE LETRAS ────────────────────────────────────────────────
//
// `escala` não é enfeite. Duas fontes no mesmo corpo em pixels NÃO parecem do
// mesmo tamanho: o que o olho lê é a altura-de-x, e ela varia muito entre
// famílias. A Caveat é manuscrita e tem altura-de-x baixa — em 17,5px fica
// miúda ao lado da Geist; a Garamond, clássica, também pede mais. Sem este
// ajuste, trocar de letra pareceria trocar de tamanho junto.
//
// As três primeiras são as de sempre, com as mesmas chaves: nota já escrita
// não muda de aparência.
const FONTES = [
  { key: 'serif',    label: 'Instrument Serif',    cara: 'editorial',       escala: 1.06, familia: "'Instrument Serif', Georgia, serif" },
  { key: 'literata', label: 'Literata',            cara: 'para ler em tela', escala: 0.97, familia: "'Literata', Georgia, serif" },
  { key: 'lora',     label: 'Lora',                cara: 'traço caligráfico', escala: 1,   familia: "'Lora', Georgia, serif" },
  { key: 'garamond', label: 'EB Garamond',         cara: 'clássica de livro', escala: 1.12, familia: "'EB Garamond', Georgia, serif" },
  { key: 'sans',     label: 'Geist',               cara: 'limpa',           escala: 1,    familia: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif" },
  { key: 'legivel',  label: 'Atkinson Hyperlegible', cara: 'a mais legível', escala: 0.97, familia: "'Atkinson Hyperlegible', -apple-system, sans-serif" },
  { key: 'mono',     label: 'Geist Mono',          cara: 'máquina',         escala: 0.95, familia: "'Geist Mono', ui-monospace, monospace" },
  { key: 'mao',      label: 'Caveat',              cara: 'manuscrita',      escala: 1.34, familia: "'Caveat', 'Segoe Script', cursive" },
];
const TAMANHOS = [
  { key: 'p',  label: 'Pequena', px: 15.5 },
  { key: 'm',  label: 'Média', px: 17.5 },
  { key: 'g',  label: 'Grande', px: 20 },
  { key: 'gg', label: 'Extra grande', px: 24 },
];
// ── ENTRELINHA ─────────────────────────────────────────────────────────
//
// Os degraus são os do Documentos do Google, e o padrão também: 1,15. A folha
// vinha em 1,72 — respirava demais, e num caderno de celular isso come metade
// da página.
//
// Mora no BLOCO, não no trecho: entrelinha é propriedade da linha, não existe
// "meia palavra mais espaçada". Por isso a escolha vale para os parágrafos que
// a seleção toca, e não para o pedaço de texto.
const ESPACOS = [
  { key: '1',   label: 'Simples', valor: 1 },
  { key: '115', label: '1,15',    valor: 1.15, dica: 'padrão' },
  { key: '15',  label: '1,5',     valor: 1.5 },
  { key: '2',   label: 'Duplo',   valor: 2 },
];

const LAYOUTS = [
  { key: 'lado',   label: 'Lado a lado', dica: 'folheia para o lado' },
  { key: 'baixo',  label: 'Empilhadas',  dica: 'uma embaixo da outra' },
  { key: 'rolo',   label: 'Folha única', dica: 'rola sem cortar' },
];

const CHAVE_PREF = 'forjamente.caderno.prefs';

const acharFonte = (k) => FONTES.find((f) => f.key === k) || FONTES[0];
const acharTam = (k) => TAMANHOS.find((t) => t.key === k) || TAMANHOS[1];

// ── NENHUMA LETRA RECEBE TEXTO ANTES DE CHEGAR ──────────────────────────
//
// Esta é a causa de uma família inteira de defeitos que parecia sem explicação:
// escolher uma letra e continuar escrevendo APAGAVA o que tinha acabado de ser
// marcado (`Antes abcd<span class="f-literata"></span>` — a palavra sumia), o
// texto digitado escapava para fora do trecho, e três tentativas de consertar o
// "daqui pra frente" morreram do mesmo jeito.
//
// As letras do caderno são preguiçosas: o arquivo só baixa quando algo na tela
// a usa. Enquanto ele não chega, o navegador desenha aquele texto INVISÍVEL por
// um instante — e o motor de edição do Chromium trata texto invisível como "não
// renderizado", e texto não renderizado ele descarta na operação seguinte. Na
// primeira vez que alguém escolhe uma letra numa sessão, o arquivo ainda está a
// caminho; no celular, com rede lenta, o instante é longo.
//
// Medido, com tudo igual e só a carga mudando:
//   letra já carregada → <span class="f-garamond">palavra abcd</span>
//   letra a caminho    → Antes abcd<span class="f-literata"></span>
//
// Por isso o caderno só abre para escrever depois de as letras que o
// documento usa terem chegado, e escolher uma letra espera o arquivo dela. O
// itálico entra junto: é outro arquivo, e o mesmo instante invisível.
const familiaDe = (chave) => acharFonte(chave).familia.split(',')[0].replace(/['"]/g, '').trim();

function garantirLetras(chaves, limite) {
  const fontes = (typeof document !== 'undefined' && (document as any).fonts) || null;
  if (!fontes || !fontes.load) return Promise.resolve();
  const pedidos = [...new Set(chaves.filter(Boolean))].flatMap((k) => {
    const f = familiaDe(k);
    return [fontes.load('400 17px "' + f + '"'), fontes.load('italic 400 17px "' + f + '"')]
      // Uma falha de rede não pode travar o caderno: segue sem aquela letra.
      .map((p) => p.catch(() => null));
  });
  return Promise.race([
    Promise.all(pedidos),
    new Promise((r) => setTimeout(r, limite)),
  ]);
}

/** As letras que um documento usa: a base da folha e a de cada trecho. */
function letrasDoDocumento(documento, base) {
  const usadas = new Set([base]);
  for (const b of documento || []) for (const p of (b.c || [])) if (p.f) usadas.add(p.f);
  return [...usadas];
}

/** Os nós que correspondem UM-A-UM aos blocos do documento.
 *
 *  Não dá para usar `children` direto: itens seguidos viram uma `<ul>` só (do
 *  contrário cada item seria uma lista de um item, com o marcador espaçado).
 *  Então uma `<ul>` de três itens é UM filho e TRÊS blocos. Quem conta página e
 *  quem procura o cursor precisam da conta certa, e é esta. */
function unidades(raiz) {
  const fora = [];
  if (!raiz) return fora;
  for (const filho of Array.from(raiz.children)) {
    const t = (filho.tagName || '').toUpperCase();
    if (t === 'UL' || t === 'OL') fora.push(...Array.from(filho.children));
    else fora.push(filho);
  }
  return fora;
}

/** As folhas antigas viradas num documento só.
 *
 *  A paginação destrutiva cortava parágrafo no meio de frase e guardava as
 *  metades em linhas diferentes. Emendar de volta é olhar a costura: se a folha
 *  termina sem pontuação e a seguinte começa em minúscula, aquilo era uma frase
 *  só. Onde a costura não parece corte, os blocos ficam separados — que é o
 *  caso de quem de fato escreveu dois parágrafos. */
export function juntarFolhas(docs) {
  const saida = [];
  const textoDe = (b) => (b && b.c ? b.c.map((p) => p.s).join('') : '');
  for (const d of docs) {
    const blocos = (Array.isArray(d) ? d : []).map((b) => ({ ...b, c: [...(b.c || [])] }));
    if (!blocos.length) continue;
    const ultimo = saida[saida.length - 1];
    const primeiro = blocos[0];
    const esq = textoDe(ultimo);
    const dir = textoDe(primeiro);
    const cortado = !!ultimo && !!primeiro
      && ultimo.t === 'p' && primeiro.t === 'p'
      && !!esq.trim() && !!dir.trim()
      && !/[.!?…:;"'’”)\]]\s*$/.test(esq)
      && /^[a-zà-öø-ÿ,;)"'’”]/.test(dir);
    if (cortado) {
      // O corte por palavra comia o espaço da costura: a sobra era colada com
      // `replace(/^\s+/, '')` e o que ficou perdeu o separador. Devolve um.
      const emenda = /\s$/.test(esq) || /^\s/.test(dir) ? [] : [{ s: ' ' }];
      ultimo.c = [...ultimo.c, ...emenda, ...primeiro.c];
      saida.push(...blocos.slice(1));
      continue;
    }
    saida.push(...blocos);
  }
  return saida;
}

/** A FOLHA DE ESTILO DA FOLHA, montada UMA vez.
 *
 *  Ficava dentro do componente, e por isso era remontada a cada render —
 *  alguns milhares de caracteres de CSS reconstruídos por template literal
 *  toda vez que qualquer estado mudasse. Arrastar a barra de ferramentas
 *  dispara um evento de rolagem por quadro, e cada um deles refazia isto:
 *  o arrasto saía aos trancos.
 *
 *  Nada aqui depende de estado — só das listas fechadas de letras, tamanhos e
 *  entrelinhas —, então o lugar certo é fora, e uma vez só. */
const CSS_DA_FOLHA = `
        /* ── LETRA E TAMANHO, EM CLASSES ──────────────────────────────
           (sem crase neste comentário: ele vive dentro de um template literal,
           e uma crase aqui fecharia a string no meio do CSS.)

           f-lora, z-gg: o documento guarda a CHAVE, e o que a chave faz mora
           aqui. Em nenhum momento um texto guardado vira estilo — o pior que
           uma chave inventada consegue é não casar com regra nenhuma.

           --escala é o truque que faz as duas conversarem. Duas famílias no
           mesmo corpo em pixels não parecem do mesmo tamanho, porque o que o
           olho lê é a altura-de-x. Cada família declara a sua escala; o
           tamanho é calculado com ela. Como variável de CSS é herdada, o
           elemento do tamanho precisa estar POR DENTRO do da família — e é
           por isso que doc.ts embrulha nessa ordem. */
        .folha-rica { --escala: 1; }
        ${FONTES.map((f) => `
        .folha-rica.f-${f.key}, .folha-rica .f-${f.key} {
          font-family: ${f.familia};
          --escala: ${f.escala};
        }`).join('')}
        ${TAMANHOS.map((t) => `
        .folha-rica.z-${t.key}, .folha-rica .z-${t.key} {
          font-size: calc(${t.px}px * var(--escala));
        }`).join('')}

        /* O <font> QUE O NAVEGADOR ACABOU DE CRIAR.
           Os comandos de letra e tamanho produzem <font face="serif"> e
           <font size="4">, e o navegador OBEDECE esses atributos: "serif" foi
           lido como a família genérica serif, e "4" como o quarto degrau da
           escala antiga do HTML. O trecho marcado saía na fonte errada e num
           tamanho que não era o pedido.

           Estas regras retomam o comando. Atributo de apresentação tem
           especificidade zero, então qualquer regra de folha de estilo ganha
           dele — sem precisar mexer no DOM logo depois do comando, o que
           arriscaria o cursor.

           Estes <font> são transitórios: na próxima leitura viram f/z no
           documento, e ao reabrir a folha o desenho sai por classe. */
        ${ESPACOS.map((e) => `
        .folha-rica.e-${e.key}, .folha-rica .e-${e.key} { line-height: ${e.valor}; }`).join('')}

        ${FONTES.map((f) => `
        .folha-rica font[face="${f.key}"] {
          font-family: ${f.familia};
          --escala: ${f.escala};
        }`).join('')}
        ${TAMANHOS.map((t) => `
        .folha-rica font[size="${PARA_TAMANHO[t.key]}"] {
          font-size: calc(${t.px}px * var(--escala));
        }`).join('')}

        .folha-rica {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
          font-kerning: normal;
          word-break: break-word;
        }
        .folha-rica:empty:before {
          content: attr(data-vazio);
          color: ${PAPEL.fraco};
          pointer-events: none;
        }
        .folha-rica h2 { font-size: 1.24em; font-weight: 700; margin: .5em 0 .2em; }
        .folha-rica h3 { font-size: 1.09em; font-weight: 700; margin: .45em 0 .15em; }
        .folha-rica blockquote {
          margin: .3em 0; padding-left: 12px;
          border-left: 2px solid ${PAPEL.margem}; font-style: italic;
        }
        .folha-rica ul, .folha-rica ol { margin: .3em 0; padding-left: 1.35em; }
        .folha-rica li { margin: .05em 0; }
        .folha-rica mark { background: rgba(232,122,59,.26); color: inherit; border-radius: 2px; }
        .folha-rica u { text-underline-offset: 2px; }
        .barra-ferramentas::-webkit-scrollbar { display: none; }
        /* O realce da busca não é um elemento: é pintura por cima do texto,
           via API de realce do CSS. Some junto com a busca, e nunca vira
           conteúdo da nota. */
        ::highlight(${CLASSE_REALCE}) {
          background: rgba(232,122,59,.32);
          color: inherit;
        }
`;

export const CadernoFolha: React.FC<{
  session: any;
  caderno: { id: string; name: string };
  onVoltar: () => void;
}> = ({ session, caderno, onVoltar }) => {
  // O DOCUMENTO é o estado. Não há lista de folhas: há um documento e uma lista
  // de CORTES — o índice do bloco onde cada página começa. `cortes` nunca é
  // gravado; é a conta da vitrine.
  const [doc, setDoc] = useState([]);
  const [cortes, setCortes] = useState([0]);
  const [nota, setNota] = useState({ id: null, font: null, fontSize: null });
  const [carregando, setCarregando] = useState(true);
  const [ativa, setAtiva] = useState(0);
  // A FOLHA QUE ESTÁ NA TELA não é a folha ativa. `ativa` é onde as
  // ferramentas agem (a última que teve o cursor); esta é a que o olho vê, e é
  // ela que o "FOLHA X DE Y" do cabeçalho conta. Rolar até a última folha
  // continuava dizendo "FOLHA 1" porque o cabeçalho lia `ativa`.
  const [naTela, setNaTela] = useState(0);
  // A altura da gaveta aberta, para o papel ganhar folga embaixo do mesmo
  // tamanho — sem isso a última linha não tem para onde subir.
  const [alturaGaveta, setAlturaGaveta] = useState(0);
  const [modo, setModo] = useState('edicao');
  const [estado, setEstado] = useState('limpo');
  const [gaveta, setGaveta] = useState(null);      // 'fonte' | 'tamanho' | 'espaco' | 'folhas'
  const [marcas, setMarcas] = useState({});
  const [outras, setOutras] = useState(0);
  const [aviso, setAviso] = useState(null);
  // Recado que NÃO tranca a escrita (o `aviso` tranca): rascunho recuperado,
  // conflito resolvido. A pessoa lê, fecha, e segue escrevendo.
  const [recado, setRecado] = useState(null);
  // Quanto do teto do documento já foi usado, em %, a partir de 90.
  const [teto, setTeto] = useState(0);
  // A BUSCA. `achados` guarda posições no DOM, não texto: elas valem enquanto a
  // folha não for redesenhada, e por isso a busca é refeita a cada tecla.
  const [busca, setBusca] = useState({ aberta: false, termo: '', achados: [], atual: 0 });
  // A BARRA NÃO CABE NA TELA, E ISSO PRECISA APARECER.
  //
  // São 18 ferramentas em 718px de largura; um iPhone 14 Pro tem 393. Letra,
  // Tamanho, Entrelinha e Folhas nascem 157px DEPOIS da borda direita — e nada
  // dizia que a barra rola. Quem quisesse trocar a letra tinha de descobrir o
  // gesto por acidente. O esmaecido na borda é o sinal, e ele some quando não
  // há mais nada para o lado.
  const [barraNoFim, setBarraNoFim] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    const base = { layout: 'rolo', fonte: 'serif', tamanho: 'm' };
    try { return { ...base, ...JSON.parse(localStorage.getItem(CHAVE_PREF) || '{}') }; }
    catch { return base; }
  });

  const areasRef = useRef({});
  const palcoRef = useRef(null);
  const medidorRef = useRef(null);
  const campoBusca = useRef(null);
  const docRef = useRef([]);
  const cortesRef = useRef([0]);
  const notaRef = useRef({ id: null, font: null, fontSize: null });
  // ── NUNCA PERDER TEXTO ───────────────────────────────────────────────
  // `baseRef`  — de qual versão do servidor este documento partiu (updated_at).
  //              Vai junto de cada salvamento: se o servidor tiver uma mais
  //              nova, ele recusa em vez de sobrescrever (ver o PATCH).
  // `sujoRef`  — há coisa escrita que o servidor ainda não tem.
  // `avisoRef` — espelho de `aviso` para quem roda fora do render (sair,
  //              trocar de app), onde o estado do React chega velho.
  const baseRef = useRef(null);
  const sujoRef = useRef(false);
  const avisoRef = useRef(null);
  avisoRef.current = aviso;
  const sessaoRef = useRef(session);
  sessaoRef.current = session;
  const relogioRascunho = useRef(null);
  const relogioRetentar = useRef(null);
  const chaveRascunho = 'forjamente.caderno.rascunho.' + caderno.id;
  // `versao` sobe quando o conteúdo das janelas precisa ser REPOSTO: carregar e
  // repaginar mudam; digitar NÃO — repor a cada tecla jogaria o cursor para o
  // começo da folha.
  const versao = useRef(1);
  const revisao = useRef(1);
  const relogioSalvar = useRef(null);
  const relogioPaginar = useRef(null);
  const gavetaRef = useRef(null);
  // A HISTÓRIA DO DOCUMENTO — ver "DESFAZER É DO DOCUMENTO" mais abaixo.
  const historia = useRef({ desfazer: [], refazer: [], grupo: null, em: 0 });
  const cursorAntes = useRef(null);
  const tipoDaEntrada = useRef(null);
  // Uma ferramenta da barra está agindo: o `input` que o execCommand dispara
  // não é a pessoa escrevendo (e não fecha a gaveta).
  const porFerramenta = useRef(false);

  const [medida, setMedida] = useState({ altura: 520 });
  const [medido, setMedido] = useState(false);
  const [fontesProntas, setFontesProntas] = useState(false);
  // No celular o teclado sobe e cobre o rodapé. `visualViewport` diz qual é a
  // altura REALMENTE visível; sem isso a barra de ferramentas fica atrás do
  // teclado justo quando a mão está escrevendo.
  const [fundo, setFundo] = useState(0);

  const paginado = prefs.layout !== 'rolo';
  const letraBase = acharFonte(nota.font || prefs.fonte).key;
  const corpoBase = acharTam(nota.fontSize || prefs.tamanho).key;
  const classesFolha = `folha-rica f-${letraBase} z-${corpoBase} e-115`;

  useEffect(() => {
    const vv = (window as any).visualViewport;
    if (!vv) return;
    const medir = () => setFundo(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    medir();
    vv.addEventListener('resize', medir);
    vv.addEventListener('scroll', medir);
    return () => { vv.removeEventListener('resize', medir); vv.removeEventListener('scroll', medir); };
  }, []);

  // A CONTA DA PÁGINA ESPERA A LETRA CHEGAR.
  //
  // As letras do caderno são preguiçosas de propósito (só baixam quando alguém
  // as usa), e `font-display: swap` desenha em Georgia enquanto isso. Georgia é
  // ~12% mais larga que a Instrument Serif: medir antes da troca dá uma página
  // a menos de texto, e a conta mudaria sozinha meio segundo depois.
  useEffect(() => {
    let vivo = true;
    const pronto = () => { if (vivo) setFontesProntas(true); };
    const fontes = (document as any).fonts;
    if (!fontes || !fontes.ready) { pronto(); return undefined; }
    fontes.ready.then(pronto).catch(pronto);
    // Rede ruim não pode travar o caderno: passados 2,5s, desenha assim mesmo.
    const t = setTimeout(pronto, 2500);
    return () => { vivo = false; clearTimeout(t); };
  }, []);

  // `styleWithCSS` desligado faz o editor produzir <b> e <i> em vez de spans
  // com estilo. É o que mantém a lista branca curta e o corpo limpo.
  useEffect(() => { try { document.execCommand('styleWithCSS', false, false); } catch {} }, []);

  // ── ABRIR O CADERNO ──────────────────────────────────────────────────
  // Voz e foto NÃO são folhas de papel — ficam contadas no pé e acessíveis pela
  // lista, em vez de sumirem.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        // `paginas: '1'` porque a listagem comum NAO devolve pagina — aqui e
        // justamente o lugar onde elas sao o conteudo.
        const r = await api.listarNotas(session, { caderno: caderno.id, ordem: '1', paginas: '1' });
        if (!vivo) return;
        const todas = r.notas || [];
        const texto = todas.filter((n) => !n.audioUrl && !n.photoUrl);
        setOutras(todas.length - texto.length);

        const folha = texto[0] || null;
        let documento = folha ? paraDocumento(folha) : [];

        // ── A UNIFICAÇÃO ────────────────────────────────────────────────
        // Caderno escrito antes desta mudança chega partido em N linhas. Juntar
        // acontece UMA vez, aqui, e a conversão é daqui porque só este lado sabe
        // ler folha em markdown, em html e em documento.
        let unificado = null;
        if (texto.length > 1) {
          documento = juntarFolhas(texto.map((n) => paraDocumento(n)));
          try {
            unificado = await api.unificarCaderno(session, caderno.id, {
              text: documento,
              manter: folha.id,
              remover: texto.slice(1).map((n) => n.id),
            });
          } catch (e) {
            // NÃO escreve nada por cima enquanto o servidor não confirmar: um
            // salvamento aqui gravaria o texto juntado numa folha e deixaria as
            // outras vivas, e a próxima abertura juntaria tudo de novo — em
            // dobro. Melhor abrir só para leitura e tentar na próxima vez.
            if (!vivo) return;
            docRef.current = documento;
            setDoc(documento);
            setNota({ id: null, font: folha.font, fontSize: folha.fontSize });
            setModo('leitura');
            setAviso('Reorganizando este caderno. Ele abre só para leitura até terminar.');
            setCarregando(false);
            return;
          }
        }

        if (!vivo) return;
        baseRef.current = (unificado && unificado.updatedAt) || (folha && folha.updatedAt) || null;

        // ── O RASCUNHO QUE FICOU NO APARELHO ────────────────────────────
        // Igual ao servidor: já foi salvo, apaga. Partiu da versão que está no
        // servidor: é texto que não chegou a subir — volta para a tela e sobe.
        // Partiu de uma versão que o servidor JÁ NÃO TEM (outro aparelho
        // escreveu depois): vira um caderno à parte, e nada se perde.
        const rascunho = lerRascunho();
        let precisaSalvar = false;
        if (rascunho && Array.isArray(rascunho.doc)) {
          const doRascunho = validar(rascunho.doc);
          if (paraTexto(doRascunho) === paraTexto(documento)) {
            apagarRascunho();
          } else if ((rascunho.base || null) === (baseRef.current || null)) {
            documento = doRascunho;
            sujoRef.current = true;
            precisaSalvar = true;
            setRecado('Recuperamos o que você tinha escrito aqui e ainda não estava salvo.');
          } else {
            try {
              const nome = await guardarComoCopia(doRascunho);
              apagarRascunho();
              setRecado('Este caderno mudou em outro aparelho desde a última vez que você escreveu aqui. '
                + 'O que tinha ficado neste aparelho está no caderno “' + nome + '”.');
            } catch { /* sem rede: o rascunho fica, e a próxima abertura tenta de novo */ }
          }
        }
        if (!vivo) return;
        docRef.current = documento;
        historia.current = { desfazer: [], refazer: [], grupo: null, em: 0 };
        setDoc(documento);
        medirTeto(documento);
        const info = {
          id: folha ? folha.id : null,
          font: (folha && folha.font) || prefs.fonte,
          fontSize: (folha && folha.fontSize) || prefs.tamanho,
        };
        notaRef.current = info;
        setNota(info);
        // As letras DESTE documento antes de abrir para escrever; as outras da
        // coleção vêm atrás, sem ninguém esperar — quando a pessoa abrir a
        // gaveta, quase sempre já chegaram.
        await garantirLetras(letrasDoDocumento(documento, info.font), 4000);
        if (!vivo) return;
        garantirLetras(FONTES.map((f) => f.key), 15000);
        versao.current++;
        revisao.current++;
        if (precisaSalvar) agendarSalvar();
      } catch {
        if (!vivo) return;
        docRef.current = [];
        setDoc([]);
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, caderno.id]);

  // ── SEMEAR UMA JANELA ────────────────────────────────────────────────
  // O conteúdo entra por nós, nunca por `innerHTML`, e só quando a chave muda.
  // A chave junta versão e faixa de blocos: digitar não a muda (senão o cursor
  // saltaria a cada tecla), repaginar e carregar mudam.
  //
  // A SEMEADURA MORA NO REF, e não num efeito. Cada modo aninha as folhas de um
  // jeito (num papel só, ou um papel por folha), então trocar de modo faz o
  // React DESMONTAR os editores e montar outros: elementos novos, vazios. Um
  // efeito com lista de dependências não roda para encher os novos, e o caderno
  // aparecia em branco até sair e entrar. No ref isso não tem como acontecer:
  // ele roda quando o elemento nasce, seja qual for a razão.
  const semear = (el, chave, blocos) => {
    if (!el) return;
    if (el.dataset.semente === chave) return;
    el.dataset.semente = chave;
    el.innerHTML = '';
    el.appendChild(paraDom(blocos));
  };

  // TROCAR DE PAGINAÇÃO COM O TECLADO ABERTO.
  //
  // Cada modo aninha as folhas de um jeito, então o React desmonta o editor
  // que tinha foco e monta outro — e foco num elemento que saiu da tela é
  // teclado fechado. A posição é guardada ANTES da troca e devolvida num
  // `useLayoutEffect`, ainda dentro do gesto, que é a condição do iOS para o
  // teclado não descer.
  const cursorDaTroca = useRef(null);
  const trocarLayout = (chave) => {
    cursorDaTroca.current = editorComFoco() ? posicaoDoCursor() : null;
    // Ir para folha única com cortes de várias páginas desenharia só a
    // primeira por um instante (até a repaginação): o documento todo precisa
    // caber na janela única JÁ.
    if (chave === 'rolo' && cortesRef.current.length !== 1) {
      cortesRef.current = [0];
      versao.current++;
      setCortes([0]);
    }
    mudarPref('layout', chave);
    setGaveta(null);
  };

  const mudarPref = (k, v) => {
    const novo = { ...prefs, [k]: v };
    setPrefs(novo);
    try { localStorage.setItem(CHAVE_PREF, JSON.stringify(novo)); } catch {}
  };

  // ── GUARDAR ──────────────────────────────────────────────────────────
  // Uma folha no banco, um salvamento. A fila existe porque a primeira gravação
  // é que CRIA a linha: dois salvamentos cruzados antes de o primeiro voltar
  // veriam `id` nulo e criariam duas. Aconteceu de verdade quando cada página
  // era uma linha, e a fila é o que impede a repetição.
  const fila = useRef(Promise.resolve());

  // ── O RASCUNHO NO APARELHO ───────────────────────────────────────────
  //
  // O salvamento no servidor espera 1,2s de pausa — e entre a última tecla e
  // esse salvamento cabem o "voltar" do Android, trocar de app, o sistema
  // matar o Forjamente em segundo plano. Medido em 01/10/2026: os dois
  // primeiros perdiam as últimas palavras. O rascunho fica no próprio
  // aparelho, é escrito logo depois de cada pausa curta e só é apagado quando
  // o servidor confirma. Ao abrir, ele é conferido contra o servidor.
  const lerRascunho = () => {
    try { return JSON.parse(localStorage.getItem(chaveRascunho) || 'null'); } catch { return null; }
  };
  const apagarRascunho = () => { try { localStorage.removeItem(chaveRascunho); } catch {} };
  const escreverRascunho = () => {
    if (!sujoRef.current) return;
    try {
      localStorage.setItem(chaveRascunho, JSON.stringify({
        doc: docRef.current, base: baseRef.current, quando: Date.now(),
      }));
    } catch { /* aparelho sem espaço: o salvamento no servidor segue valendo */ }
  };
  const agendarRascunho = () => {
    clearTimeout(relogioRascunho.current);
    relogioRascunho.current = setTimeout(escreverRascunho, 300);
  };

  // ── O LIMITE AVISA ANTES ─────────────────────────────────────────────
  // Contado do mesmo jeito que `validar` corta (letras dos pedaços, blocos),
  // com os MESMOS números importados de doc.ts.
  const medirTeto = (documento) => {
    let letras = 0;
    for (const b of documento) for (const p of (b.c || [])) letras += (p.s || '').length;
    const uso = Math.max(letras / MAX_TEXTO, (documento.length || 0) / MAX_BLOCOS);
    setTeto(uso >= 0.9 ? Math.min(100, Math.floor(uso * 100)) : 0);
  };

  /** Passa a mostrar uma versão vinda do servidor — a de outro aparelho. */
  const carregarVersao = (atual) => {
    const documento = paraDocumento(atual);
    docRef.current = documento;
    // A HISTÓRIA ERA DA VERSÃO DAQUI. Sem zerar, Desfazer traria de volta o
    // texto deste aparelho por cima do que veio do outro — e o salvamento,
    // já partindo da versão nova, gravaria sem conflito nenhum.
    historia.current = { desfazer: [], refazer: [], grupo: null, em: 0 };
    baseRef.current = atual.updatedAt || null;
    sujoRef.current = false;
    apagarRascunho();
    cursorPendente.current = null;
    cortesRef.current = [0];
    versao.current++;
    revisao.current++;
    setCortes([0]);
    setDoc(documento);
    medirTeto(documento);
    agendarPaginar();
  };

  /** Guarda um documento como caderno à parte. Devolve o nome, ou null. */
  const guardarComoCopia = async (documento) => {
    const nome = (caderno.name + ' — versão deste aparelho').slice(0, 80);
    const c = await api.criarCaderno(sessaoRef.current, { name: nome });
    const info = notaRef.current;
    await api.criarNotaLivre(sessaoRef.current, {
      notebookId: c.id, text: JSON.stringify(documento), formato: 'doc',
      font: info.font, fontSize: info.fontSize, ordem: 0,
    });
    return nome;
  };

  // ── O CONFLITO NÃO APAGA NINGUÉM ─────────────────────────────────────
  //
  // O servidor recusou: outro aparelho escreveu neste caderno depois da versão
  // de onde este partiu. Sobrescrever apagaria o que foi escrito lá; descartar
  // apagaria o que foi escrito aqui. Então a versão DAQUI vira um caderno à
  // parte ("… — versão deste aparelho") e a tela passa a mostrar a de lá.
  // Se nem a cópia der para criar, nada é trocado: o texto continua na tela e
  // no rascunho do aparelho, e a pessoa é avisada.
  const resolverConflito = async (atual, meu) => {
    if (paraTexto(paraDocumento(atual)) === paraTexto(meu)) {
      baseRef.current = atual.updatedAt || null;
      sujoRef.current = false;
      apagarRascunho();
      setEstado('salvo');
      return;
    }
    try {
      const nome = await guardarComoCopia(meu);
      carregarVersao(atual);
      setEstado('limpo');
      setRecado('Este caderno foi alterado em outro aparelho e passou a mostrar aquela versão. '
        + 'O que você escreveu aqui não se perdeu: está no caderno “' + nome + '”.');
    } catch {
      setEstado('sujo');
      setRecado('Este caderno foi alterado em outro aparelho e não deu para guardar a sua versão agora. '
        + 'Ela continua aqui e no rascunho deste aparelho — tente de novo em instantes.');
    }
  };

  const gravar = useCallback(async (opcoes = {}) => {
    const documento = docRef.current;
    const info = notaRef.current;
    const vazio = !paraTexto(documento).trim();
    if (vazio && !info.id) return;            // caderno em branco não nasce no banco
    const revisaoNoInicio = revisao.current;
    const sessao = sessaoRef.current;

    setEstado('salvando');
    try {
      const campos = {
        text: JSON.stringify(documento), formato: 'doc',
        font: info.font, fontSize: info.fontSize, ordem: 0,
      };
      if (info.id) {
        // `keepalive` deixa o pedido sobreviver à página sendo fechada — mas
        // o navegador só o aceita até 64 KB de corpo. Acima disso, o rascunho
        // do aparelho é a garantia.
        const extra = opcoes.urgente && campos.text.length < 60000 ? { keepalive: true } : {};
        const r = await api.editarNota(sessao, info.id,
          { ...campos, ...(baseRef.current ? { seAtualizadoEm: baseRef.current } : {}) }, extra);
        if (r && r.updatedAt) baseRef.current = r.updatedAt;
      } else {
        const n = await api.criarNotaLivre(sessao, { notebookId: caderno.id, ...campos });
        notaRef.current = { ...info, id: n.id };
        baseRef.current = n.updatedAt || null;
        setNota((a) => ({ ...a, id: n.id }));
      }
      // Só limpa se nada mudou enquanto o pedido voava: o que foi escrito no
      // meio ainda não está no servidor.
      if (revisao.current === revisaoNoInicio) {
        sujoRef.current = false;
        apagarRascunho();
      }
      clearTimeout(relogioRetentar.current);
      setEstado('salvo');
      setTimeout(() => setEstado((e) => (e === 'salvo' ? 'limpo' : e)), 1600);
    } catch (e) {
      if (e && e.status === 409 && e.dados && e.dados.atual) {
        await resolverConflito(e.dados.atual, documento);
        return;
      }
      // Falhou (rede, servidor): diz na tela e tenta de novo sozinho. O
      // rascunho do aparelho segura o texto enquanto isso.
      setEstado('sujo');
      escreverRascunho();
      clearTimeout(relogioRetentar.current);
      relogioRetentar.current = setTimeout(() => { if (sujoRef.current) guardarRef.current(); }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caderno.id]);

  const guardar = useCallback((opcoes) => {
    fila.current = fila.current.then(() => gravar(opcoes)).catch(() => {});
    return fila.current;
  }, [gravar]);
  // Quem roda fora do render (sair, trocar de app, desmontar) pega sempre a
  // versão atual — a do primeiro render teria a sessão de quando abriu.
  const guardarRef = useRef(guardar);
  guardarRef.current = guardar;

  const agendarSalvar = () => {
    clearTimeout(relogioSalvar.current);
    relogioSalvar.current = setTimeout(() => guardarRef.current(), 1200);
  };

  // ── SAIR NUNCA ESPERA O RELÓGIO ──────────────────────────────────────
  //
  // Antes, desmontar o caderno CANCELAVA o salvamento agendado: o "voltar" do
  // Android e a navegação do app levavam as últimas palavras junto. Agora todo
  // caminho de saída descarrega na hora o que estiver pendente.
  const descarregar = (urgente) => {
    clearTimeout(relogioSalvar.current);
    if (!sujoRef.current || avisoRef.current) return;
    escreverRascunho();
    guardarRef.current({ urgente });
  };

  useEffect(() => {
    // Trocar de app: o sistema pode matar o Forjamente em segundo plano a
    // qualquer momento depois disto.
    const aoEsconder = () => {
      if (document.visibilityState === 'hidden') descarregar(true);
      else conferirRef.current();
    };
    const aoSair = () => descarregar(true);
    document.addEventListener('visibilitychange', aoEsconder);
    window.addEventListener('pagehide', aoSair);
    return () => {
      document.removeEventListener('visibilitychange', aoEsconder);
      window.removeEventListener('pagehide', aoSair);
      clearTimeout(relogioPaginar.current);
      clearTimeout(relogioRascunho.current);
      clearTimeout(relogioRetentar.current);
      descarregar(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── VOLTOU AO APP: O OUTRO APARELHO ESCREVEU? ────────────────────────
  // O caso comum do "dois aparelhos": o tablet ficou com o caderno aberto, a
  // pessoa escreveu no celular e voltou ao tablet. Sem nada pendente aqui, a
  // versão nova simplesmente aparece — antes de a pessoa digitar por cima.
  const conferirSeMudou = async () => {
    if (sujoRef.current || !notaRef.current.id || avisoRef.current) return;
    try {
      const r = await api.listarNotas(sessaoRef.current, { caderno: caderno.id, ordem: '1', paginas: '1' });
      const linha = (r.notas || []).find((n) => n.id === notaRef.current.id);
      if (!linha || sujoRef.current) return;
      if (linha.updatedAt && linha.updatedAt !== baseRef.current) carregarVersao(linha);
    } catch { /* sem rede: segue com o que tem */ }
  };

  const conferirRef = useRef(conferirSeMudou);
  conferirRef.current = conferirSeMudou;

  const sair = () => {
    descarregar(false);
    onVoltar();
  };

  // ── ONDE O CURSOR ESTÁ, EM COORDENADAS DO DOCUMENTO ──────────────────
  //
  // Repaginar repõe o conteúdo das janelas, e sem isto o cursor voltaria para o
  // começo da folha no meio de uma frase. Guardar a posição em (bloco, letra)
  // do DOCUMENTO — e não do elemento — é o que a faz sobreviver à mudança de
  // recorte: o bloco continua sendo o mesmo bloco, esteja ele na página 2 ou 3.
  /** Um ponto do DOM em coordenadas do documento: (bloco, letra). */
  const pontoNoDocumento = (alvo, deslocamento) => {
    let k = -1;
    let editor = null;
    for (const chave of Object.keys(areasRef.current)) {
      const el = areasRef.current[chave];
      if (el && el.contains(alvo)) { k = Number(chave); editor = el; break; }
    }
    if (!editor) return null;
    const us = unidades(editor);
    let indice = -1;
    for (let i = 0; i < us.length; i++) {
      if (us[i] === alvo || us[i].contains(alvo)) { indice = i; break; }
    }
    if (indice < 0) return null;
    const ate = document.createRange();
    ate.selectNodeContents(us[indice]);
    try { ate.setEnd(alvo, deslocamento); } catch (e) { return null; }
    return { bloco: (cortesRef.current[k] || 0) + indice, letra: ate.toString().length };
  };

  /** OS DOIS EXTREMOS, e não só o começo.
   *
   *  Guardava-se apenas o início, então toda repaginação COLAPSAVA a seleção:
   *  quem tivesse um trecho marcado e esperasse meio segundo ficava com um
   *  cursor, e a ferramenta seguinte não tinha sobre o que agir. O caso é mais
   *  comum do que parece no celular, onde encostar na barra fecha o teclado e
   *  isso sozinho repagina. */
  const posicaoDoCursor = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const faixa = sel.getRangeAt(0);
    const inicio = pontoNoDocumento(faixa.startContainer, faixa.startOffset);
    if (!inicio) return null;
    const fim = faixa.collapsed ? inicio : pontoNoDocumento(faixa.endContainer, faixa.endOffset);
    return { ...inicio, fim: fim || inicio };
  };

  /** O nó de texto e o deslocamento que correspondem a (bloco, letra). */
  const noDoPonto = (ponto) => {
    const cs = cortesRef.current;
    let k = 0;
    for (let i = 0; i < cs.length; i++) if (cs[i] <= ponto.bloco) k = i;
    const el = areasRef.current[k];
    if (!el) return null;
    const us = unidades(el);
    const u = us[ponto.bloco - cs[k]] || us[us.length - 1];
    if (!u) return null;
    const andarilho = document.createTreeWalker(u, NodeFilter.SHOW_TEXT, null);
    let resto = ponto.letra;
    while (andarilho.nextNode()) {
      const t = andarilho.currentNode;
      const n = (t.nodeValue || '').length;
      if (resto <= n) return { el, no: t, onde: Math.max(0, Math.min(resto, n)) };
      resto -= n;
    }
    return { el, unidade: u };
  };

  const porCursorEm = (pos) => {
    if (!pos) return;
    const comeco = noDoPonto(pos);
    if (!comeco) return;
    const termino = pos.fim && pos.fim !== pos ? noDoPonto(pos.fim) : comeco;
    comeco.el.focus();
    const r = document.createRange();
    if (comeco.no) r.setStart(comeco.no, comeco.onde);
    else { r.selectNodeContents(comeco.unidade); r.collapse(true); }
    if (termino && termino.no) {
      try { r.setEnd(termino.no, termino.onde); } catch (e) { r.collapse(true); }
    } else if (comeco.no) {
      r.collapse(true);
    }
    const s = window.getSelection();
    s.removeAllRanges();
    s.addRange(r);
  };

  // ── A CONTA DA PÁGINA ────────────────────────────────────────────────
  //
  // Mede UMA vez, num papel escondido do tamanho exato do de verdade, e decide
  // os cortes de uma passada só. A versão anterior media a folha na tela e
  // tirava o excesso DELA até caber — ou seja, precisava mexer no conteúdo para
  // descobrir quanto cabia. Aqui a medição acontece longe do que foi escrito.
  //
  // Bloco que sozinho é maior que a página FICA INTEIRO, e o papel cresce. É o
  // oposto do que se fazia antes (partir a palavra), e é o que papel faz.
  const alturaUtil = () => Math.max(120, medida.altura - RESPIRO.topo - RESPIRO.baixo);

  // O CURSOR VOLTA DEPOIS QUE O DESENHO ACONTECEU.
  //
  // A primeira tentativa devolvia o cursor num `requestAnimationFrame` logo
  // após `setCortes`. Não dá: o quadro pode chegar ANTES de o React ter posto
  // as janelas novas no lugar, e aí o cursor era reposto na folha velha — o
  // teste flagrou escrevendo "Folha dois." no começo da folha 1 quando devia
  // estar na 2. `useLayoutEffect` roda depois da troca do DOM e antes de a tela
  // ser pintada, que é exatamente a janela certa.
  const cursorPendente = useRef(null);

  const aplicarCortes = (novos) => {
    const atuais = cortesRef.current;
    if (atuais.length === novos.length && atuais.every((c, i) => c === novos[i])) return false;
    // SÓ COM O TECLADO ABERTO. Devolver o cursor dá foco ao editor; com o
    // teclado fechado, uma repaginação qualquer — o ResizeObserver, a letra
    // que chegou — abria o teclado sozinha.
    cursorPendente.current = editorComFoco() ? posicaoDoCursor() : null;
    cortesRef.current = novos;
    versao.current++;
    setCortes(novos);
    return true;
  };

  useLayoutEffect(() => {
    const pos = cursorDaTroca.current;
    if (!pos) return;
    cursorDaTroca.current = null;
    if (modo === 'edicao') { porCursorEm(pos); manterCursorAVista(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.layout]);

  // DEVOLVER O CURSOR NÃO BASTA: ELE TEM DE FICAR À VISTA.
  //
  // Repaginar leva a linha que se escreve para a folha seguinte, que fica mais
  // abaixo na rolagem. O cursor voltava ao lugar certo do documento, mas a
  // tela não ia atrás: em Empilhadas, depois da pausa, a linha ia parar atrás
  // da barra de ferramentas (medido: linha em 846px, barra começando em 801,
  // numa tela de 852). `manterCursorAVista` só rodava na tecla, e a pausa não
  // é tecla.
  useLayoutEffect(() => {
    const pos = cursorPendente.current;
    if (!pos) return;
    cursorPendente.current = null;
    // Só devolve o cursor a quem estava escrevendo: em leitura, mexer na
    // seleção abriria o teclado do celular sozinho.
    if (modo === 'edicao') { porCursorEm(pos); manterCursorAVista(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cortes]);

  const paginar = () => {
    if (!paginado) { aplicarCortes([0]); return; }
    const med = medidorRef.current;
    if (!med || !medido || carregando) return;
    semear(med, 'medidor:' + revisao.current, docRef.current);
    const us = unidades(med);
    if (!us.length) { aplicarCortes([0]); return; }
    const base = med.getBoundingClientRect().top;
    const util = alturaUtil();
    const novos = [0];
    let topo = 0;
    for (let i = 0; i < us.length; i++) {
      const caixa = us[i].getBoundingClientRect();
      if (caixa.bottom - base - topo > util && i > novos[novos.length - 1]) {
        novos.push(i);
        topo = caixa.top - base;
      }
    }
    aplicarCortes(novos);
  };

  const paginarRef = useRef(paginar);
  paginarRef.current = paginar;

  const agendarPaginar = () => {
    clearTimeout(relogioPaginar.current);
    // Pelo ref: quem agenda pode ser um ouvinte do primeiro render (voltar ao
    // app, conflito no salvamento), e o `paginar` daquele render ainda acha que
    // o caderno está carregando.
    relogioPaginar.current = setTimeout(() => paginarRef.current(), 400);
  };

  // Repagina quando muda aquilo de que a conta depende: o modo, a altura da
  // folha, a letra que enfim chegou — e ao carregar. Nunca a cada tecla: isso é
  // do relógio de `agendarPaginar`.
  useEffect(() => {
    if (carregando) return undefined;
    const t = setTimeout(paginar, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregando, medido, fontesProntas, prefs.layout, medida.altura, letraBase, corpoBase]);

  // ── ESCREVER ─────────────────────────────────────────────────────────
  // A janela devolve os blocos dela; o documento é recomposto em volta. Os
  // cortes seguintes andam pelo tanto que o número de blocos mudou, para que a
  // página 3 continue começando onde começava.
  const anotar = (k, origem) => {
    const el = areasRef.current[k];
    if (!el) return;
    const cs = cortesRef.current;
    const ini = cs[k] || 0;
    const fim = k + 1 < cs.length ? cs[k + 1] : docRef.current.length;
    const blocos = deDom(el);
    // O comando da barra é lido duas vezes (pelo `input` que ele dispara e
    // pela própria ferramenta): a segunda leitura não muda nada e não pode
    // virar um passo vazio no Desfazer.
    if (JSON.stringify(blocos) !== JSON.stringify(docRef.current.slice(ini, fim))) {
      registrar(docRef.current, origem);
    }
    const novo = [...docRef.current.slice(0, ini), ...blocos, ...docRef.current.slice(fim)];
    const delta = blocos.length - (fim - ini);
    let piso = ini + blocos.length;
    const novosCortes = cs.map((c, j) => {
      if (j <= k) return c;
      const movido = Math.min(Math.max(piso, c + delta), novo.length);
      piso = movido;
      return movido;
    });
    docRef.current = novo;
    cortesRef.current = novosCortes;
    revisao.current++;
    sujoRef.current = true;
    setDoc(novo);
    setCortes(novosCortes);
    medirTeto(novo);
    agendarRascunho();
    agendarSalvar();
    agendarPaginar();
  };

  const digitou = (k) => {
    anotar(k);
    // ESCREVER FECHA A GAVETA. Ela abre para escolher; escolhido, a pessoa
    // volta a escrever — e a gaveta aberta cobria justamente a linha onde ela
    // escrevia. O `input` que a própria ferramenta dispara não conta.
    if (!porFerramenta.current) setGaveta((g) => (g ? null : g));
    manterCursorAVista();
  };

  // ── DESFAZER É DO DOCUMENTO, NÃO DO NAVEGADOR ───────────────────────
  //
  // O Desfazer era o do navegador (`execCommand('undo')`), e a pilha do
  // navegador mora no DOM do editor. Repaginar REPÕE o conteúdo das janelas
  // (`semear`), e o navegador não sabe desfazer o que ele não fez: depois da
  // primeira pausa que mudasse uma folha de lugar, Desfazer não fazia nada.
  // Medido: 26 blocos em Empilhadas, 30 linhas digitadas (nasce uma folha),
  // seis toques em Desfazer — nada voltava.
  //
  // A história agora é do DOCUMENTO: cada passo guarda o documento como ele
  // era e onde o cursor estava, em coordenadas do documento (bloco, letra) —
  // as mesmas que já sobrevivem à repaginação. Desfazer é repor esse
  // documento e repaginar, o que funciona em qualquer modo e depois de
  // qualquer número de repaginações.
  //
  // Custo de memória baixo: um passo é um array de referências; os blocos que
  // a edição não tocou são OS MESMOS objetos do passo anterior.
  //
  // Os passos se agrupam como num editor de texto: letras seguidas são um
  // passo só; Enter começa um passo novo (Desfazer tira a última linha, não a
  // última letra); uma pausa de 1s também; apagar e escrever não se misturam;
  // cada ferramenta é um passo próprio.
  const LIMITE_DA_HISTORIA = 200;

  const registrar = (antes, origem) => {
    const h = historia.current;
    const agora = Date.now();
    const tipo = origem || tipoDaEntrada.current || 'escrever';
    const agrupa = tipo === 'escrever' || tipo === 'apagar';
    const continua = agrupa && h.grupo === tipo && agora - h.em < 1000;
    if (!continua) {
      h.desfazer.push({ doc: antes, cursor: cursorAntes.current });
      if (h.desfazer.length > LIMITE_DA_HISTORIA) h.desfazer.shift();
    }
    h.refazer = [];
    h.grupo = agrupa ? tipo : null;
    h.em = agora;
    tipoDaEntrada.current = null;
  };

  /** Põe um documento inteiro no lugar do atual — Desfazer, Refazer e a
   *  emenda entre folhas passam por aqui. As janelas são repostas (versão
   *  nova), o cursor volta em coordenadas do documento SÓ se o teclado estava
   *  aberto (a regra do teclado vale também aqui), e a conta da página é
   *  refeita. */
  const porDocumento = (documento, cursor, cortesNovos) => {
    clearTimeout(relogioPaginar.current);
    const comFoco = editorComFoco();
    let cs = (cortesNovos || cortesRef.current).filter((c, i) => i === 0 || c < documento.length);
    cs = cs.filter((c, i) => i === 0 || c > cs[i - 1]);
    docRef.current = documento;
    cortesRef.current = cs;
    revisao.current++;
    versao.current++;
    cursorPendente.current = comFoco && cursor ? { ...cursor, fim: cursor.fim || cursor } : null;
    // Desfazer é escrever: o mesmo caminho de guarda de `anotar` — sujo,
    // rascunho no aparelho, salvamento —, senão um Desfazer seguido de sair
    // do app não chegaria ao servidor.
    sujoRef.current = true;
    setDoc(documento);
    // Array novo SEMPRE, mesmo com os mesmos números: é a troca de identidade
    // que roda o efeito que devolve o cursor.
    setCortes([...cs]);
    medirTeto(documento);
    agendarRascunho();
    agendarSalvar();
    agendarPaginar();
  };

  const andarNaHistoria = (de, para) => {
    const h = historia.current;
    const alvo = h[de].pop();
    if (!alvo) return;
    const agora = editorComFoco() ? posicaoDoCursor() : null;
    h[para].push({ doc: docRef.current, cursor: agora });
    h.grupo = null;
    porDocumento(alvo.doc, alvo.cursor || agora);
  };
  const desfazer = () => andarNaHistoria('desfazer', 'refazer');
  const refazer = () => andarNaHistoria('refazer', 'desfazer');

  // ── A EMENDA ENTRE FOLHAS ────────────────────────────────────────────
  //
  // Cada folha é um editor separado, e o navegador não sabe que a folha 2
  // continua a 1: Backspace no começo de uma folha não fazia nada, e as
  // setas paravam na borda. Em Folha única tudo funcionava porque lá é um
  // editor só. A emenda é feita no MODELO, como o navegador faria dentro de
  // uma folha:
  //
  //   · a linha de baixo vai para o fim da de cima, e o cursor fica na emenda;
  //   · se a de cima está vazia, ela some e a de baixo sobe inteira;
  //   · item de lista colado num bloco que não é da mesma lista primeiro vira
  //     parágrafo (é o que o Backspace faz dentro de uma folha).
  const textoDoBloco = (b) => (b && b.c ? b.c.map((p) => p.s).join('') : '');

  /** Junta o bloco `b` ao bloco `b - 1`. `antes` é onde o cursor estava,
   *  para o Desfazer devolvê-lo ali. */
  const emendar = (b, antes) => {
    const d = docRef.current;
    if (b <= 0 || b >= d.length) return false;
    const cima = d[b - 1];
    const baixo = d[b];
    const lista = baixo.t === 'li' || baixo.t === 'oli';
    let novo;
    let cursor;
    let cs = cortesRef.current;
    if (lista && cima.t !== baixo.t) {
      novo = [...d.slice(0, b), { ...baixo, t: 'p' }, ...d.slice(b + 1)];
      cursor = { bloco: b, letra: 0 };
    } else {
      const textoCima = textoDoBloco(cima);
      const junto = textoCima ? { ...cima, c: [...cima.c, ...baixo.c] } : { ...baixo };
      novo = [...d.slice(0, b - 1), junto, ...d.slice(b + 1)];
      cursor = { bloco: b - 1, letra: textoCima.length };
      // O bloco `b` deixou de existir: as folhas que começavam depois dele
      // começam uma posição antes. A que começava NELE passa a começar no
      // bloco seguinte — que agora tem o mesmo índice —, e a linha emendada
      // fica na folha de cima, com o cursor.
      cs = cs.map((c) => (c > b ? c - 1 : c));
    }
    historia.current.desfazer.push({ doc: d, cursor: antes || { bloco: b, letra: 0 } });
    if (historia.current.desfazer.length > LIMITE_DA_HISTORIA) historia.current.desfazer.shift();
    historia.current.refazer = [];
    historia.current.grupo = null;
    porDocumento(novo, cursor, cs);
    return true;
  };

  /** Apagar na borda da folha k — para trás no começo, para a frente no fim —
   *  emenda com a folha vizinha. Devolve se emendou. */
  const emendaNaBorda = (k, sentido) => {
    if (!paginado) return false;
    const pos = posicaoDoCursor();
    if (!pos || pos.fim.bloco !== pos.bloco || pos.fim.letra !== pos.letra) return false;
    const [ini, fim] = faixaDaFolha(k);
    if (sentido === 'tras' && k > 0 && pos.bloco === ini && pos.letra === 0) return emendar(ini, pos);
    if (sentido === 'frente' && k + 1 < cortesRef.current.length
      && pos.bloco === fim - 1 && pos.letra >= textoDoBloco(docRef.current[fim - 1]).length) {
      return emendar(fim, pos);
    }
    return false;
  };

  /** O índice da janela (folha) que contém um nó, ou -1. */
  const folhaDoNo = (no) => {
    for (const chave of Object.keys(areasRef.current)) {
      const el = areasRef.current[chave];
      if (el && (el === no || el.contains(no))) return Number(chave);
    }
    return -1;
  };

  /** Os limites da folha k no documento: [primeiro bloco, depois do último). */
  const faixaDaFolha = (k) => {
    const cs = cortesRef.current;
    return [cs[k] || 0, k + 1 < cs.length ? cs[k + 1] : docRef.current.length];
  };

  // O `beforeinput` é o único aviso que chega ANTES de o navegador mexer no
  // texto, e chega em todo lugar: no teclado físico, no virtual do iPhone e no
  // do Android — onde o `keydown` do Backspace vem como "Unidentified" e não
  // serve para nada. É por ele que a emenda e o Desfazer do sistema (o menu,
  // o chacoalhar do iPhone) entram.
  const antesDeEntrar = useRef(null);
  antesDeEntrar.current = (e) => {
    const k = folhaDoNo(e.target);
    if (k < 0 || modo !== 'edicao') return;
    const t = e.inputType || '';
    if (t === 'historyUndo') { e.preventDefault(); desfazer(); return; }
    if (t === 'historyRedo') { e.preventDefault(); refazer(); return; }
    if (!e.isComposing) {
      const sentido = /^delete.*Backward$/.test(t) ? 'tras' : /^delete.*Forward$/.test(t) ? 'frente' : null;
      if (sentido && emendaNaBorda(k, sentido)) { e.preventDefault(); return; }
    }
    cursorAntes.current = posicaoDoCursor();
    if (t === 'insertParagraph' || t === 'insertLineBreak') {
      // Enter começa um passo novo na história.
      historia.current.grupo = null;
      tipoDaEntrada.current = 'escrever';
    } else if (t.startsWith('delete')) {
      tipoDaEntrada.current = 'apagar';
    } else if (t.startsWith('format')) {
      tipoDaEntrada.current = 'formatar';
    } else {
      tipoDaEntrada.current = 'escrever';
    }
  };
  useEffect(() => {
    const ouvir = (e) => { if (antesDeEntrar.current) antesDeEntrar.current(e); };
    document.addEventListener('beforeinput', ouvir, true);
    return () => document.removeEventListener('beforeinput', ouvir, true);
  }, []);

  /** As setas atravessam a emenda; Ctrl+Z e Ctrl+Shift+Z usam a história do
   *  documento. Seta com Shift fica com o navegador: a seleção não atravessa
   *  dois editores, e é por isso também que "selecionar tudo" pega só a folha
   *  onde o cursor está. */
  const teclaNaFolha = (e, k) => {
    const mod = e.ctrlKey || e.metaKey;
    const letra = (e.key || '').toLowerCase();
    if (mod && !e.altKey && letra === 'z') { e.preventDefault(); if (e.shiftKey) refazer(); else desfazer(); return; }
    if (mod && !e.altKey && !e.shiftKey && letra === 'y') { e.preventDefault(); refazer(); return; }
    if (!paginado || mod || e.altKey || e.shiftKey) return;
    // A emenda também pelo `keydown`: nem todo navegador avisa `beforeinput`
    // quando não há nada para apagar dentro do editor — e na borda da folha
    // não há. (No Android o `keydown` do Backspace não diz qual tecla é; lá
    // quem resolve é o `beforeinput`.)
    if ((e.key === 'Backspace' || e.key === 'Delete') && !e.isComposing) {
      if (emendaNaBorda(k, e.key === 'Backspace' ? 'tras' : 'frente')) e.preventDefault();
      return;
    }
    const frente = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    const tras = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
    if (!frente && !tras) return;
    const vizinha = k + (frente ? 1 : -1);
    if (vizinha < 0 || vizinha >= cortesRef.current.length || !areasRef.current[vizinha]) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !sel.isCollapsed) return;
    const pos = posicaoDoCursor();
    if (!pos) return;
    const [ini, fim] = faixaDaFolha(k);
    let naBorda = false;
    if (e.key === 'ArrowRight') {
      naBorda = pos.bloco === fim - 1 && pos.letra >= textoDoBloco(docRef.current[fim - 1]).length;
    } else if (e.key === 'ArrowLeft') {
      naBorda = pos.bloco === ini && pos.letra === 0;
    } else {
      // Para cima e para baixo a borda é a LINHA, não a letra: estar na
      // última linha da folha já basta para descer para a próxima.
      const us = unidades(areasRef.current[k]);
      const u = frente ? us[us.length - 1] : us[0];
      const caixa = caixaDoCursor(sel.getRangeAt(0));
      if (u && caixa) {
        const b = u.getBoundingClientRect();
        const meia = Math.max(4, caixa.height / 2);
        naBorda = frente
          ? pos.bloco === fim - 1 && caixa.bottom + meia > b.bottom
          : pos.bloco === ini && caixa.top - meia < b.top;
      }
    }
    if (!naBorda) return;
    e.preventDefault();
    const [iniV, fimV] = faixaDaFolha(vizinha);
    const destino = frente
      ? { bloco: iniV, letra: 0 }
      : { bloco: fimV - 1, letra: textoDoBloco(docRef.current[fimV - 1]).length };
    porCursorEm({ ...destino, fim: destino });
    manterCursorAVista();
  };

  // ── COLAR ────────────────────────────────────────────────────────────
  // O único caminho por onde marcação de fora entraria. Em vez de deixar o
  // navegador enfiar o HTML da área de transferência dentro do editor, ele é
  // lido como DOCUMENTO num documento inerte e devolvido como nós construídos a
  // partir do modelo. O negrito de um site colado continua negrito; a
  // `<img onerror>` do mesmo site não vira nada, porque não existe pedaço de
  // imagem onde ela caiba.
  const colar = (e, k) => {
    e.preventDefault();
    const dados = e.clipboardData;
    if (!dados) return;
    const marcado = dados.getData('text/html');
    const simples = dados.getData('text/plain');
    const documento = marcado
      ? deHtml(marcado)
      : String(simples || '').split(/\r?\n/).map((l) => ({ t: 'p', c: l ? [{ s: l }] : [] }));

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    cursorAntes.current = posicaoDoCursor();
    tipoDaEntrada.current = 'colar';
    const r = sel.getRangeAt(0);
    r.deleteContents();
    const pedaco = paraDom(documento);
    const ultimo = pedaco.lastChild;
    r.insertNode(pedaco);
    if (ultimo) {
      const fim = document.createRange();
      fim.selectNodeContents(ultimo);
      fim.collapse(false);
      sel.removeAllRanges();
      sel.addRange(fim);
    }
    digitou(k);
  };

  /** A caixa da linha onde o cursor (o fim da faixa) está. */
  const caixaDoCursor = (faixa) => {
    const r = faixa.cloneRange();
    r.collapse(false);
    let caixa = r.getBoundingClientRect();
    // Cursor em linha vazia não tem caixa própria: vale a do bloco.
    if (!caixa || (!caixa.height && !caixa.top)) {
      let no = r.startContainer;
      if (no.nodeType === 1 && no.childNodes.length) {
        // Cursor ENTRE blocos (o contêiner é o próprio editor): a caixa é a
        // do bloco ao lado, não a da folha inteira.
        const filho = no.childNodes[Math.min(r.startOffset, no.childNodes.length - 1)];
        if (filho && filho.nodeType === 1) no = filho;
      }
      const bloco = no.nodeType === 1 ? no : no.parentElement;
      caixa = bloco ? bloco.getBoundingClientRect() : null;
    }
    return caixa && caixa.height ? caixa : null;
  };

  /** A faixa do cursor, se ele estiver numa folha: a viva ou, com o teclado
   *  fechado, a que foi guardada no toque da barra. */
  const faixaDoCursor = () => {
    const dentro = (r) => r && folhaDoNo(r.startContainer) >= 0;
    const sel = window.getSelection();
    if (sel && sel.rangeCount && dentro(sel.getRangeAt(0))) return sel.getRangeAt(0);
    const g = selecaoGuardada.current;
    return dentro(g) ? g : null;
  };

  /** O fim da área onde a linha ainda se lê: o topo da barra de ferramentas
   *  ou, aberta, o da gaveta. MEDIDO, e não estimado: a barra cresce com o
   *  recorte do iPhone, e a gaveta com o que tem dentro. */
  const chaoVisivel = () => {
    const palco = palcoRef.current;
    let chao = palco ? palco.getBoundingClientRect().bottom : window.innerHeight - fundo;
    for (const el of [barraRef.current, gavetaRef.current]) {
      if (el) chao = Math.min(chao, el.getBoundingClientRect().top);
    }
    return chao - 10;
  };

  /** Mantém o cursor à vista enquanto se escreve.
   *
   *  A barra de ferramentas flutua sobre a folha, e o teclado sobe por baixo
   *  dela. Sem isto, escrever perto do fim da folha empurra a linha para trás
   *  da barra: a pessoa continua digitando sem ver o que digita.
   *
   *  Vale também para cima (depois de desfazer, a linha pode ter ficado acima
   *  da tela) e, no lado a lado, para o lado: a folha do cursor vira para a
   *  tela. */
  const manterCursorAVista = () => {
    const faixa = faixaDoCursor();
    const palco = palcoRef.current;
    if (!faixa || !palco) return;
    const caixa = caixaDoCursor(faixa);
    if (!caixa) return;

    const p = palco.getBoundingClientRect();
    const chao = chaoVisivel();
    const teto = p.top + 8;
    if (caixa.bottom > chao) palco.scrollTop += caixa.bottom - chao + 12;
    else if (caixa.top < teto) palco.scrollTop -= teto - caixa.top + 12;

    // LADO A LADO: a folha do cursor precisa estar na tela, e não ao lado.
    if (prefs.layout === 'lado' && (caixa.left < p.left || caixa.right > p.right)) {
      const k = folhaDoNo(faixa.startContainer);
      const papel = k >= 0 && areasRef.current[k] ? areasRef.current[k].parentElement : null;
      if (papel) palco.scrollLeft += papel.getBoundingClientRect().left - p.left - 16;
    }
  };

  // ── A ALTURA DA FOLHA ────────────────────────────────────────────────
  // A folha ocupa a tela toda menos o respiro. Todas iguais nos dois modos
  // paginados — e só cresce quando um bloco sozinho não cabe, que é quando
  // cortar seria pior.
  useEffect(() => {
    const el = palcoRef.current;
    if (!el) return undefined;
    const medir = () => {
      // A ALTURA DA FOLHA NÃO SEGUE O TECLADO.
      //
      // Se seguisse, abrir o teclado encolheria a folha e o recorte mudaria no
      // meio da escrita: as páginas dançariam sob a mão. A conta sai da tela
      // INTEIRA (`innerHeight`, que não muda com o teclado) menos o que está
      // acima do palco e o respiro de baixo. O teclado encolhe a ÁREA DE
      // ROLAGEM, não a folha.
      const cs = getComputedStyle(el);
      const acima = el.getBoundingClientRect().top;
      const respiro = parseFloat(cs.paddingTop || '0') + parseFloat(cs.paddingBottom || '0');
      const util = window.innerHeight - acima - respiro;
      setMedida({ altura: Math.max(240, Math.round(util)) });
      setMedido(true);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
    // `modo` entra porque a barra aparece só em edição e muda o que sobra.
  }, [modo]);

  // ── FERRAMENTAS ──────────────────────────────────────────────────────
  //
  // ── A FERRAMENTA PRECISA DE UMA JANELA QUE EXISTA ──────────────────
  //
  // `ativa` é um índice de página, e página vai e vem: repaginar pode deixar
  // duas onde havia três. Quem estava escrevendo na 3 ficava com `ativa = 2`
  // apontando para uma janela que não existe mais, e aí TODA ferramenta virava
  // um botão morto — `areasRef.current[ativa]` era `undefined` e a função saía
  // pela porta dos fundos, sem fazer nada e sem dizer nada.
  //
  // No computador isso quase não acontece. No celular, encostar na barra fecha
  // o teclado, o que muda a altura útil, o que repagina — exatamente no toque
  // em que a pessoa queria trocar a letra.
  const folhaAtiva = () => {
    if (areasRef.current[ativa]) return ativa;
    const vivas = Object.keys(areasRef.current).map(Number).filter((n) => areasRef.current[n]);
    if (!vivas.length) return 0;
    // A mais próxima da que se perdeu — no fim das contas, a última.
    return vivas.sort((a, b) => Math.abs(a - ativa) - Math.abs(b - ativa))[0];
  };

  // ── A SELEÇÃO SOBREVIVE AO DEDO ──────────────────────────────────────
  //
  // No computador, `preventDefault` no mousedown do botão impede que ele roube
  // o cursor do texto. No celular esse mousedown é SINTETIZADO depois do toque,
  // e cancelá-lo não devolve nada: o toque já tirou o foco do editor, e em
  // alguns navegadores cancelar o mousedown sintetizado mata o `click` junto —
  // o botão fica desenhado e morto.
  //
  // Então em vez de impedir a perda, guarda-se o que havia e devolve-se na
  // hora de agir. Funciona nos dois mundos, e não depende de o navegador
  // sintetizar evento nenhum.
  const selecaoGuardada = useRef(null);

  const lembrarSelecao = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const r = sel.getRangeAt(0);
    const el = areasRef.current[folhaAtiva()];
    if (el && el.contains(r.commonAncestorContainer)) selecaoGuardada.current = r.cloneRange();
  };

  // ── ARRASTAR A BARRA ─────────────────────────────────────────────────
  // No celular o dedo empurra a área rolável sozinho. No computador não existe
  // esse gesto: arrastar com o botão do mouse não rola nada, e a roda vertical
  // não anda para o lado. Numa janela estreita, as quatro últimas ferramentas
  // ficavam inalcançáveis — foi o que o Wesley encontrou na prévia.
  const barraRef = useRef(null);
  const barraArrasto = useRef(null);
  const arrastouDeVerdade = useRef(false);

  // O ARRASTO OUVE A JANELA, E NÃO A BARRA.
  //
  // Ouvindo só a barra, bastava o ponteiro subir um pouco — sair da faixa de
  // 50px de altura — para o arrasto morrer no meio. Era o "não dá pra arrastar
  // até o final": a mão continuava andando e a barra tinha parado de escutar.
  // Com os ouvintes na janela, o gesto vale até soltar o botão, onde quer que
  // o ponteiro esteja.
  //
  // Sem `setPointerCapture` de propósito: capturar o ponteiro faz o `click`
  // seguinte ser entregue à BARRA e não ao botão, e aí nenhuma ferramenta
  // responderia a um clique simples.
  const pegarBarra = (e) => {
    const el = e.currentTarget;
    if (el.scrollWidth <= el.clientWidth) return;
    arrastouDeVerdade.current = false;
    const estado = { x: e.clientX, inicio: el.scrollLeft, el };
    barraArrasto.current = estado;

    const mover = (ev) => {
      const dx = ev.clientX - estado.x;
      // Três pixels de folga: mão parada treme, e um tremor não é um arrasto.
      if (Math.abs(dx) > 3) arrastouDeVerdade.current = true;
      estado.el.scrollLeft = estado.inicio - dx;
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
      barraArrasto.current = null;
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
  };

  // O ESMAECIDO SÓ APARECE QUANDO HÁ MAIS COISA PARA O LADO.
  //
  // A primeira versão só media no `onScroll` — e `onScroll` nunca dispara numa
  // barra que não rola. Numa janela larga, onde as 18 ferramentas cabem, a
  // borda ficava esmaecida para sempre, prometendo um trecho que não existe.
  useEffect(() => {
    const el = barraRef.current;
    if (!el) return undefined;
    const medir = () => {
      const sobra = el.scrollWidth - el.clientWidth;
      setBarraNoFim(sobra <= 4 || el.scrollLeft >= sobra - 4);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [modo, fundo]);

  // ── O TECLADO É DE QUEM ESCREVE ──────────────────────────────────────
  //
  // A regra, nas palavras do Wesley: a barra de ferramentas NUNCA mexe no
  // teclado. Aberto, continua aberto; fechado, continua fechado. Ele abre só
  // quando a pessoa toca no texto para escrever.
  //
  // No celular "teclado aberto" é exatamente "o editor tem foco": o teclado
  // virtual sobe quando um elemento editável ganha foco e desce quando ele
  // perde. Então a regra vira duas proibições:
  //
  //   · tocar na barra não pode TIRAR o foco do editor — é o `preventDefault`
  //     no mousedown de cada botão (ver a barra lá embaixo);
  //   · nenhuma ferramenta pode DAR foco ao editor — e todas davam: cada uma
  //     começava com `el.focus()`, que no celular abre o teclado.
  const editorComFoco = () => {
    const ativo = document.activeElement;
    return !!ativo && Object.values(areasRef.current).includes(ativo);
  };

  /** A seleção sobre a qual agir: a viva, se estiver neste editor; senão a que
   *  foi guardada no toque da barra. */
  const faixaNoEditor = (el) => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount) {
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) return r.cloneRange();
    }
    const g = selecaoGuardada.current;
    if (g && el.contains(g.commonAncestorContainer)) return g.cloneRange();
    return null;
  };

  // ── AGIR SEM ABRIR O TECLADO ─────────────────────────────────────────
  //
  // `execCommand` precisa do editor com foco para saber onde agir. Com o
  // teclado fechado, dar foco o abriria — então o foco é dado com
  // `inputmode="none"`, que pede ao sistema para NÃO mostrar teclado, e tirado
  // na mesma volta do laço de eventos, antes de qualquer pintura. A pessoa vê o
  // negrito aparecer; o teclado nem chega a ser pedido.
  const silencioso = useRef(false);
  const semTeclado = (el, fazer) => {
    const antes = el.getAttribute('inputmode');
    silencioso.current = true;
    el.setAttribute('inputmode', 'none');
    try {
      try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
      fazer(true);
    } finally {
      el.blur();
      if (antes === null) el.removeAttribute('inputmode');
      else el.setAttribute('inputmode', antes);
      silencioso.current = false;
    }
  };

  const comando = (cmd, valor) => {
    const k = folhaAtiva();
    const el = areasRef.current[k];
    if (!el) return;
    const alvo = faixaNoEditor(el);
    const agir = (forcar) => {
      const sel = window.getSelection();
      const atual = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
      // No modo silencioso o foco acabou de pôr o cursor no começo da folha:
      // a faixa certa precisa ser reposta MESMO estando "dentro do editor".
      if (alvo && (forcar || !atual || !el.contains(atual.commonAncestorContainer))) {
        try { sel.removeAllRanges(); sel.addRange(alvo); } catch (e) { /* faixa envelhecida */ }
      }
      porFerramenta.current = true;
      try { document.execCommand(cmd, false, valor); } catch {} finally { porFerramenta.current = false; }
    };
    if (!editorComFoco() && !alvo) {
      // Teclado fechado e nenhum lugar marcado no texto: não há sobre o que
      // agir, e abrir o teclado para criar um lugar seria exatamente o que a
      // regra proíbe.
      return;
    }
    // Onde o cursor estava ANTES — o trecho inteiro —, para o Desfazer
    // devolvê-lo ali, marcado como estava.
    if (alvo) {
      const a = pontoNoDocumento(alvo.startContainer, alvo.startOffset);
      const b = pontoNoDocumento(alvo.endContainer, alvo.endOffset);
      cursorAntes.current = a ? { ...a, fim: b || a } : null;
    } else {
      cursorAntes.current = posicaoDoCursor();
    }
    tipoDaEntrada.current = 'formatar';
    if (editorComFoco()) agir(false);
    else semTeclado(el, agir);
    anotar(k, 'formatar');
    lerMarcas();
  };

  /** O que está ligado no trecho onde o cursor está — para acender o botão. */
  const lerMarcas = () => {
    try {
      setMarcas({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strikeThrough: document.queryCommandState('strikeThrough'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        insertOrderedList: document.queryCommandState('insertOrderedList'),
        bloco: String(document.queryCommandValue('formatBlock') || '').toLowerCase(),
        // A ESCOLHA PENDENTE GANHA DO QUE ESTÁ NO DOM. Com o cursor parado
        // não há elemento nenhum para ler ainda, e sem isto a lista continuava
        // acesa na letra antiga — "clico e não seleciona nada".
        letra: (pendente.current && pendente.current.f) || noCursor('f'),
        corpo: (pendente.current && pendente.current.z) || noCursor('z'),
        espaco: noCursor('e'),
      });
    } catch {}
  };

  useEffect(() => {
    if (modo !== 'edicao') return undefined;
    const ouvir = () => lerMarcas();
    document.addEventListener('selectionchange', ouvir);
    return () => document.removeEventListener('selectionchange', ouvir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo]);

  /** Liga e desliga um bloco: tocar em "Título" de novo devolve parágrafo. */
  const bloco = (tag) => comando('formatBlock', marcas.bloco === tag ? '<div>' : `<${tag}>`);

  /** A caixa de tarefa é um caractere. Tocar nela vira e desvira — em edição e
   *  em leitura, porque riscar o que foi feito é leitura, não escrita. */
  const talvezVirarTarefa = (e, k) => {
    const pegar = (document as any).caretRangeFromPoint;
    if (!pegar) return false;
    const r = pegar.call(document, e.clientX, e.clientY);
    if (!r || r.startContainer.nodeType !== 3) return false;
    const texto = r.startContainer.nodeValue || '';
    for (const p of [r.startOffset, r.startOffset - 1]) {
      if (texto[p] === '☐' || texto[p] === '☑') {
        cursorAntes.current = pontoNoDocumento(r.startContainer, p);
        r.startContainer.nodeValue = texto.slice(0, p) + (texto[p] === '☐' ? '☑' : '☐') + texto.slice(p + 1);
        anotar(k, 'tarefa');
        return true;
      }
    }
    return false;
  };

  // ── LETRA E TAMANHO VALEM PARA O TRECHO — OU DAQUI PRA FRENTE ────────
  //
  // Com trecho selecionado, a escolha vale para ele, pelos comandos do próprio
  // navegador (`fontName`/`fontSize`): são eles que sabem partir a seleção no
  // meio de uma palavra. O `<font>` que produzem é transitório — na leitura
  // seguinte vira `f`/`z` no documento e nunca chega ao banco.
  //
  // Sem trecho, a escolha vale DAQUI PRA FRENTE: para o que a pessoa escrever
  // a seguir. O navegador cumpre isso sozinho — com o cursor parado,
  // `execCommand('fontName')` grava um "estilo de digitação" e o próximo
  // caractere nasce dentro de um `<font face>`. MEDIDO em 01/10/2026: digitar
  // depois da escolha produziu `Primeira linha.<font face="garamond">X</font>`.
  //
  // (O comentário que estava aqui dizia o contrário — que o Chromium "não faz
  // nada" com a fonte pendente. Era conclusão de uma medição errada: a sonda
  // procurava a opção por `innerText`, que vem VAZIO nas letras de carregamento
  // preguiçoso, e o clique nunca acontecia. Três tentativas de cirurgia de DOM
  // nasceram dessa conclusão errada e foram desfeitas.)
  //
  // Ele é aplicado UMA vez, na hora da escolha. Repô-lo a cada mudança de
  // seleção foi tentado e aninhava um `<font>` dentro do outro a cada palavra
  // — era paliativo para um problema cuja causa verdadeira era outra (a letra
  // ainda baixando; ver `garantirLetras`). Com a letra carregada, a digitação
  // segue dentro do `<font>` sozinha, como segue dentro de um `<b>`.
  //
  // Quando a escolha deixa de valer:
  //
  //   · a pessoa toca noutro ponto do texto COM o teclado aberto — mudou de
  //     lugar, e a escolha era para onde ela estava;
  //   · ela escolhe outra coisa, ou sai da edição.
  //
  // Com o teclado FECHADO não existe "onde ela está". Aí a escolha espera o
  // próximo toque no texto — "escolho a letra, toco onde vou escrever, escrevo"
  // — e é esse toque que ela acompanha (`esperandoToque`).
  //
  // Mora num ref, e não em estado: quem redesenha a gaveta é `lerMarcas`, que
  // já lê daqui. Um estado paralelo seria uma segunda verdade sobre a mesma
  // coisa — e duas verdades divergem na primeira pressa.
  const pendente = useRef(null);

  const aplicarPendente = () => {
    const p = pendente.current;
    if (!p || silencioso.current || !editorComFoco()) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !sel.getRangeAt(0).collapsed) return;
    porFerramenta.current = true;
    try {
      if (p.f) document.execCommand('fontName', false, p.f);
      if (p.z) document.execCommand('fontSize', false, PARA_TAMANHO[p.z] || '2');
    } catch (e) { /* navegador sem o comando */ } finally { porFerramenta.current = false; }
  };

  const marcarPendente = (qual, chave) => {
    pendente.current = {
      ...(pendente.current || {}),
      [qual]: chave,
      esperandoToque: !editorComFoco(),
    };
    aplicarPendente();
    lerMarcas();
  };

  const esquecerPendente = () => {
    if (!pendente.current) return;
    pendente.current = null;
    lerMarcas();
  };

  /** Escolher letra ou tamanho: o trecho, se houver; senão, daqui pra frente. */
  const escolher = (qual, chave, cmd, valor) => {
    const el = areasRef.current[folhaAtiva()];
    const alvo = el ? faixaNoEditor(el) : null;
    if (alvo && !alvo.collapsed) {
      esquecerPendente();
      comando(cmd, valor);
      return;
    }
    marcarPendente(qual, chave);
  };

  const aplicarLetra = (chave) => {
    mudarPref('fonte', chave);
    // ESPERA A LETRA CHEGAR. Quase sempre já chegou (o caderno pede todas ao
    // abrir) e isto resolve na hora; na primeira escolha com rede lenta, é o
    // que impede o trecho de ficar invisível e ser descartado.
    garantirLetras([chave], 3000).then(() => { escolher('f', chave, 'fontName', chave); mostrarAcimaDaGaveta(); });
  };
  const aplicarCorpo = (chave) => {
    mudarPref('tamanho', chave);
    escolher('z', chave, 'fontSize', PARA_TAMANHO[chave] || '2');
    mostrarAcimaDaGaveta();
  };

  // ── A GAVETA NÃO COBRE A LINHA ───────────────────────────────────────
  //
  // A gaveta sobe por cima da folha, e a linha onde se escreve — quase
  // sempre perto do fim do que está escrito — ficava embaixo dela (medido:
  // cursor em y=609, gaveta começando em y=434). Escolher uma letra era
  // escolher sem ver onde ela ia cair.
  //
  // Agora, escolhida a opção, o texto rola até a linha ficar ACIMA da gaveta;
  // e a gaveta fecha sozinha quando a pessoa volta a digitar (ver `digitou`).
  // Nada disso toca no foco: o teclado fica como estava.
  const mostrarAcimaDaGaveta = () => {
    requestAnimationFrame(() => manterCursorAVista());
  };

  // ── ENTRELINHA: NOS BLOCOS QUE A SELEÇÃO TOCA ────────────────────────
  // Sem comando de navegador para isto — e ainda bem, porque aqui não há nada
  // para partir: a linha inteira muda ou não muda. Mexe só em `classList`, e
  // por isso o cursor fica onde estava: nenhum nó é movido ou recriado.
  const aplicarEspaco = (chave) => {
    const k = folhaAtiva();
    const el = areasRef.current[k];
    if (!el) return;
    // SEM FOCO: entrelinha só mexe em `classList` dos blocos que a seleção
    // toca, e para isso basta saber ONDE a seleção está — não é preciso ter o
    // editor ativo. Pedir foco aqui abria o teclado à toa.
    const faixa = faixaNoEditor(el);
    if (!faixa) return;

    const tocados = unidades(el).filter((b) => {
      try { return faixa.intersectsNode(b); } catch (e) { return false; }
    });
    if (!tocados.length) return;

    cursorAntes.current = pontoNoDocumento(faixa.startContainer, faixa.startOffset);
    for (const b of tocados) {
      ESPACOS.forEach((e) => b.classList.remove('e-' + e.key));
      b.classList.add('e-' + chave);
    }
    // NÃO se grava preferência aqui, e isto não é descuido.
    //
    // Gravando, a classe da FOLHA passava a ser a escolhida — e como
    // `line-height` é herdado, os parágrafos vizinhos, que não têm classe
    // própria, iam junto. Escolher Duplo num parágrafo dobrava os três. Era o
    // "global" de volta, pela porta dos fundos.
    //
    // A base da folha é fixa em 1,15; quem quiser outra coisa marca o parágrafo,
    // e a linha seguinte nasce herdando a classe dele.
    anotar(k, 'formatar');
    lerMarcas();
    mostrarAcimaDaGaveta();
  };

  /** A letra e o tamanho do trecho onde o cursor está — para acender a opção
   *  certa na gaveta. Sobe pelos pais até achar quem mandou. */
  const noCursor = (qual) => {
    const sel = window.getSelection();
    let no = sel && sel.anchorNode;
    const lista = qual === 'f' ? FONTES.map((x) => x.key)
      : qual === 'z' ? TAMANHOS.map((x) => x.key)
      : ESPACOS.map((x) => x.key);
    while (no && no !== document.body) {
      const el = no.nodeType === 1 ? no : no.parentElement;
      if (el && el.classList) {
        for (const c of Array.from(el.classList)) {
          if (c.startsWith(qual + '-') && lista.includes(c.slice(2))) return c.slice(2);
        }
      }
      if (el && el.tagName === 'FONT') {
        if (qual === 'f' && lista.includes(el.getAttribute('face'))) return el.getAttribute('face');
        if (qual === 'z') {
          const n = { 1: 'p', 2: 'm', 3: 'g', 4: 'gg' }[el.getAttribute('size')];
          if (n) return n;
        }
      }
      no = el && el.parentElement;
    }
    // Nada marcado: vale a base da folha.
    if (qual === 'f') return letraBase;
    if (qual === 'z') return corpoBase;
    return '115';
  };

  // ── PROCURAR ─────────────────────────────────────────────────────────
  // Nada aqui escreve no DOM, e essa é a regra que decide o desenho: o que está
  // no editor é lido de volta como documento a cada tecla, então um `<mark>`
  // posto pela busca viraria CONTEÚDO da nota — procurar "casa" deixaria "casa"
  // pintada para sempre. Ver `procurar.ts`.
  // ── O FOCO VOLTA PARA A CAIXA, SEMPRE ────────────────────────────────
  //
  // Pôr a seleção dentro de um `contentEditable` FOCA o editor. Sem devolver o
  // foco, a segunda letra digitada na busca ia parar DENTRO da nota: procurar
  // "coracao" transformava "O coração tem razões" em "O racao" e destruía o
  // texto de quem estava só procurando.
  //
  // O realce de todos os achados é pintado pela API do CSS e não depende do
  // foco; a seleção fica de reserva para os navegadores sem essa API, onde ela
  // continua visível, apenas apagada.
  const mostrar = (achado) => {
    irPara(achado);
    campoBusca.current?.focus();
  };

  const buscar = (termo) => {
    const elementos = cortesRef.current.map((_, i) => areasRef.current[i]);
    const achados = procurar(elementos, termo);
    realcar(achados);
    setBusca((b) => ({ ...b, termo, achados, atual: 0 }));
    if (achados.length) mostrar(achados[0]);
  };

  const andarNaBusca = (passo) => {
    setBusca((b) => {
      if (!b.achados.length) return b;
      const atual = (b.atual + passo + b.achados.length) % b.achados.length;
      mostrar(b.achados[atual]);
      return { ...b, atual };
    });
  };

  const fecharBusca = () => {
    limparRealce();
    setBusca({ aberta: false, termo: '', achados: [], atual: 0 });
  };

  // O realce vive no navegador, fora do React: sair da tela sem apagá-lo
  // deixaria o rastro pintado em qualquer outro lugar que use a mesma API.
  useEffect(() => () => limparRealce(), []);

  /** Roda do mouse vira folhear: no computador ninguém arrasta a folha. */
  const rodaHorizontal = (e) => {
    if (prefs.layout !== 'lado') return;
    const el = e.currentTarget;
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    el.scrollLeft += e.deltaY;
  };

  const FERRAMENTAS = [
    { chave: 'undo', rotulo: '↶', titulo: 'Desfazer', fazer: desfazer, vazio: !historia.current.desfazer.length },
    { chave: 'redo', rotulo: '↷', titulo: 'Refazer', fazer: refazer, vazio: !historia.current.refazer.length },
    { divisor: true },
    { chave: 'bold', rotulo: 'B', titulo: 'Negrito', peso: 700, fazer: () => comando('bold') },
    { chave: 'italic', rotulo: 'I', titulo: 'Itálico', italico: true, fazer: () => comando('italic') },
    { chave: 'underline', rotulo: 'U', titulo: 'Sublinhado', sublinhado: true, fazer: () => comando('underline') },
    { chave: 'strikeThrough', rotulo: 'S', titulo: 'Riscado', riscado: true, fazer: () => comando('strikeThrough') },
    { divisor: true },
    { chave: 'h2', rotulo: 'T', titulo: 'Título', peso: 700, serif: true, fazer: () => bloco('h2') },
    { chave: 'h3', rotulo: 't', titulo: 'Subtítulo', peso: 600, serif: true, fazer: () => bloco('h3') },
    { chave: 'blockquote', rotulo: '❝', titulo: 'Citação', fazer: () => bloco('blockquote') },
    { divisor: true },
    { chave: 'insertUnorderedList', rotulo: '•', titulo: 'Lista', fazer: () => comando('insertUnorderedList') },
    { chave: 'insertOrderedList', rotulo: '1.', titulo: 'Lista numerada', fazer: () => comando('insertOrderedList') },
    { chave: 'tarefa', rotulo: '☐', titulo: 'Tarefa', fazer: () => comando('insertText', '☐ ') },
    { divisor: true },
    { chave: 'fonte', rotulo: 'Aa', titulo: 'Letra', serif: true, fazer: () => setGaveta((g) => (g === 'fonte' ? null : 'fonte')) },
    { chave: 'tamanho', rotulo: 'A', titulo: 'Tamanho', fazer: () => setGaveta((g) => (g === 'tamanho' ? null : 'tamanho')) },
    { chave: 'espaco', rotulo: '↕', titulo: 'Entrelinha', fazer: () => setGaveta((g) => (g === 'espaco' ? null : 'espaco')) },
    { chave: 'folhas', rotulo: '▤', titulo: 'Folhas', fazer: () => setGaveta((g) => (g === 'folhas' ? null : 'folhas')) },
  ];

  const aceso = (f) => marcas[f.chave] === true
    || (f.chave === 'h2' && marcas.bloco === 'h2')
    || (f.chave === 'h3' && marcas.bloco === 'h3')
    || (f.chave === 'blockquote' && marcas.bloco === 'blockquote')
    || gaveta === f.chave;

  // A FOLGA DA GAVETA: espaço embaixo do papel, do tamanho da gaveta aberta.
  // Sem ela, a última linha do caderno não tinha para onde subir — a rolagem
  // acabava antes de ela sair de trás da gaveta.
  const folga = modo === 'edicao' && gaveta && alturaGaveta ? alturaGaveta + 12 : 0;

  useLayoutEffect(() => {
    const el = gavetaRef.current;
    const h = gaveta && el ? Math.round(el.getBoundingClientRect().height) : 0;
    setAlturaGaveta((a) => (a === h ? a : h));
  }, [gaveta, fundo, modo]);

  // Abrir a gaveta já traz a linha para cima dela (com a folga posta); fechar
  // pode encurtar a rolagem e empurrar o texto para baixo — a linha é
  // conferida de novo.
  useEffect(() => {
    if (modo !== 'edicao') return undefined;
    const q = requestAnimationFrame(() => manterCursorAVista());
    return () => cancelAnimationFrame(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alturaGaveta]);

  // ── A FOLHA QUE ESTÁ NA TELA ─────────────────────────────────────────
  // A que ocupa mais da área visível do palco (descontada a barra, que fica
  // por cima). Só mexe no estado quando a resposta muda: rolar dispara um
  // evento por quadro.
  const medirNaTela = () => {
    const palco = palcoRef.current;
    if (!palco) return;
    const p = palco.getBoundingClientRect();
    const lado = prefs.layout === 'lado';
    const ini = lado ? p.left : p.top;
    const fim = lado ? p.right : Math.min(p.bottom, chaoVisivel() + 10);
    let melhor = 0;
    let maior = -1;
    for (let k = 0; k < cortesRef.current.length; k++) {
      const el = areasRef.current[k];
      const papel = el && el.parentElement;
      if (!papel) continue;
      const r = papel.getBoundingClientRect();
      const visto = Math.min(fim, lado ? r.right : r.bottom) - Math.max(ini, lado ? r.left : r.top);
      if (visto > maior) { maior = visto; melhor = k; }
    }
    setNaTela((n) => (n === melhor ? n : melhor));
  };
  const quadroDaRolagem = useRef(0);
  const rolouOPalco = () => {
    if (quadroDaRolagem.current) return;
    quadroDaRolagem.current = requestAnimationFrame(() => {
      quadroDaRolagem.current = 0;
      medirNaTela();
    });
  };
  useEffect(() => {
    medirNaTela();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cortes.length, prefs.layout, medida.altura, carregando, modo]);

  // ── A LISTA NUMERADA CONTINUA NA FOLHA SEGUINTE ─────────────────────
  //
  // Cada folha é desenhada só com os blocos dela, e uma `<ol>` nova começa
  // em 1: a lista de 45 itens que atravessa a página mostrava o item 33 como
  // "1.". A folha que começa no meio de uma lista recebe `start` com o número
  // CONTADO no documento — os itens `oli` seguidos logo antes dela. O número
  // não vem do que está guardado (o modelo nem tem onde guardá-lo) e não volta
  // para ele: `deDom` lê os itens, não o atributo.
  //
  // Roda a cada desenho, sem repor a folha: mexer no atributo de uma lista não
  // tira o cursor do lugar, e escrever um item novo na folha 1 renumera a 2 na
  // mesma hora.
  useLayoutEffect(() => {
    const d = docRef.current;
    const cs = cortesRef.current;
    for (let k = 0; k < cs.length; k++) {
      const el = areasRef.current[k];
      const lista = el && el.firstElementChild;
      if (!lista || lista.tagName !== 'OL') continue;
      const ini = cs[k] || 0;
      let antes = 0;
      if (d[ini] && d[ini].t === 'oli') {
        for (let i = ini - 1; i >= 0 && d[i] && d[i].t === 'oli'; i--) antes++;
      }
      if (antes) {
        if (lista.start !== antes + 1) lista.start = antes + 1;
      } else if (lista.hasAttribute('start')) {
        lista.removeAttribute('start');
      }
    }
  });

  const folhaBase = {
    background: PAPEL.fundo,
    borderRadius: 4,
    boxShadow: '0 1px 2px rgba(0,0,0,.18), 0 10px 26px -12px rgba(0,0,0,.45)',
    padding: `${RESPIRO.topo}px ${RESPIRO.direita}px ${RESPIRO.baixo}px ${RESPIRO.esquerda}px`,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  };

  /** A linha da margem — o detalhe que faz o olho reconhecer papel. Também diz
   *  onde o cursor está: na folha ativa fica em brasa. */
  const margemDe = (ativo) => (
    <div style={{
      position: 'absolute', left: 20, top: 0, bottom: 0,
      width: ativo ? 2 : 1,
      background: ativo ? 'var(--ember)' : PAPEL.margem,
      opacity: ativo ? 0.7 : 0.55,
      transition: 'background .18s, width .18s',
    }} />
  );

  const editor = (k) => {
    const cs = cortesRef.current;
    const ini = cs[k] || 0;
    const fim = k + 1 < cs.length ? cs[k + 1] : docRef.current.length;
    // A CHAVE É SÓ A VERSÃO, e isto custou um cursor.
    //
    // A primeira tentativa punha a faixa de blocos na chave (`versao:ini:fim`).
    // Parecia mais preciso e era exatamente o contrário: digitar a primeira
    // letra num caderno vazio muda `fim` de 0 para 1, a chave muda, a janela é
    // REPOSTA e o cursor volta para o começo. Escrever "Folha um." produzia
    // "olha um.F" — a primeira letra ficava para trás, sozinha, no fim.
    //
    // Versão basta porque ela sobe exatamente quando o conteúdo de uma janela
    // precisa ser reposto: ao carregar e ao repaginar. Digitar não a move, e
    // as janelas SEGUINTES não precisam ser repostas — os blocos delas são os
    // mesmos, só mudou o índice por onde começam.
    const chave = `${versao.current}:${k}`;
    return (
      <div
        data-folha={k}
        data-vazio={k === 0 ? 'Escreva…' : ''}
        ref={(el) => {
          if (!el) { delete areasRef.current[k]; return; }
          areasRef.current[k] = el;
          semear(el, chave, docRef.current.slice(ini, fim));
        }}
        className={classesFolha}
        contentEditable={modo === 'edicao'}
        suppressContentEditableWarning
        spellCheck={false}
        onInput={() => digitou(k)}
        onKeyDown={(e) => teclaNaFolha(e, k)}
        onPaste={(e) => colar(e, k)}
        onFocus={() => {
          setAtiva(k);
          // O toque que abriu o teclado é o "onde vou escrever" de uma escolha
          // feita com ele fechado. O cursor pousa um instante depois do foco —
          // daí as duas tentativas; `aplicarPendente` só age com o cursor já
          // parado dentro do editor.
          if (!silencioso.current && pendente.current) {
            // Uma vez, quando o cursor já pousou. Se ele ainda não estiver
            // parado dentro do editor, `aplicarPendente` não faz nada e a
            // segunda tentativa resolve; as duas juntas não aninham porque o
            // estilo de digitação igual ao atual não cria elemento novo.
            setTimeout(aplicarPendente, 0);
            setTimeout(aplicarPendente, 150);
          }
        }}
        onPointerDown={() => {
          const p = pendente.current;
          if (!p) return;
          // Com o teclado FECHADO, este toque é o lugar que a escolha esperava:
          // ela passa a valer aqui. Com o teclado ABERTO, é mudança de lugar, e
          // a escolha era para onde o cursor estava.
          if (p.esperandoToque && !editorComFoco()) {
            pendente.current = { ...p, esperandoToque: false };
            return;
          }
          esquecerPendente();
        }}
        onClick={(e) => {
          if (modo === 'leitura' && !aviso) {
            // Riscar a tarefa é leitura, não escrita: vira e fica em leitura.
            if (talvezVirarTarefa(e, k)) return;
            // CLICAR NUMA FOLHA EM LEITURA VOLTA A ESCREVER JÁ NELA, COM UM
            // TOQUE SÓ. É o "só clicar em cima e pronto" para corrigir a folha
            // 5 estando na 20.
            //
            // Antes o foco vinha num `requestAnimationFrame`, depois do gesto —
            // e o iOS só abre teclado para foco dado DENTRO do gesto. A edição
            // ligava, o teclado não subia, e era preciso tocar de novo. Agora
            // tudo acontece aqui, síncrono: liga a edição no próprio elemento,
            // põe o cursor onde o dedo encostou, e só então avisa o React.
            const el = e.currentTarget;
            el.setAttribute('contenteditable', 'true');
            const pegar = (document as any).caretRangeFromPoint;
            const onde = pegar ? pegar.call(document, e.clientX, e.clientY) : null;
            el.focus({ preventScroll: true });
            if (onde && el.contains(onde.startContainer)) {
              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(onde);
            }
            setModo('edicao'); setAtiva(k);
            return;
          }
          talvezVirarTarefa(e, k);
        }}
        style={{
          // Família, corpo e entrelinha saem das CLASSES f-, z- e e-, não daqui:
          // são as mesmas classes que um trecho marcado usa, então a base da
          // folha e um trecho escolhido passam pelo mesmo caminho.
          color: PAPEL.tinta,
          outline: 'none',
          // SEM ALTURA FIXA E SEM ROLAGEM INTERNA. Um parágrafo maior que a
          // página faz a folha crescer; cortá-lo foi o bug que trouxe até aqui,
          // e escondê-lo atrás de uma rolagem interna seria a mesma perda com
          // outro nome.
          minHeight: paginado ? 0 : 160,
          flex: paginado ? 1 : 'none',
          cursor: 'text',
        }}
      />
    );
  };

  return (
    <div style={{
      // TERMINA ONDE O TECLADO COMEÇA, e não no fim da tela.
      //
      // Com `inset: 0` a folha ia até embaixo sempre — inclusive quando metade
      // da tela estava coberta pelo teclado. O resultado é o do print: as
      // últimas linhas escritas ficavam atrás da barra de ferramentas, sem jeito
      // de rolar até elas, porque a área de rolagem também acabava lá embaixo,
      // fora da vista.
      position: 'fixed', top: 0, left: 0, right: 0, bottom: fundo, zIndex: 9000,
      background: 'var(--paper)', display: 'flex', flexDirection: 'column',
    }}>
      {/* A folha é a única parte do app com marcação de verdade dentro, então é
          a única que precisa de regra de estilo em folha. Fora daqui continua
          tudo em estilo por elemento. */}
      <style>{CSS_DA_FOLHA}</style>

      {/* ── topo ── */}
      {/* A FOLHA COBRE A TELA INTEIRA, inclusive o que fica atras do relogio.
          E de proposito — o papel vai ate a borda —, mas o cabecalho precisa
          descer: no iPhone 14 Pro ele nascia debaixo da Ilha Dinamica, com o
          nome do caderno atras da hora e a lupa atras da bateria.

          `env(safe-area-inset-top)` e a mesma conta que o resto do app usa, e
          vale 0 onde nao ha recorte — nada muda no computador nem no Android
          sem entalhe. */}
      <div style={{
        flex: 'none', display: 'flex', alignItems: 'center', gap: 8,
        padding: 'calc(12px + env(safe-area-inset-top, 0px)) 14px 10px',
      }}>
        <button onClick={sair} aria-label="Voltar" style={redondo}>
          <Icon name="chevron-left" size={15} color="var(--ink-2)" />
        </button>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="serif" style={{
            fontSize: 19, color: 'var(--ink)', lineHeight: 1.1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{caderno.name}</div>
          <div className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', color: 'var(--ink-4)', marginTop: 2 }}>
            {/* Falha de salvamento DIZ que falhou. Antes ficava invisível: o
                cabeçalho voltava a "FOLHA 1 DE 1" e ninguém sabia que o texto
                não tinha subido. */}
            {estado === 'sujo' ? 'NÃO SALVO · TENTANDO DE NOVO'
              : estado === 'salvando' ? 'GUARDANDO…'
              : estado === 'salvo' ? 'GUARDADO'
              : modo === 'leitura' ? 'LEITURA'
              : `FOLHA ${Math.min(naTela, cortes.length - 1) + 1} DE ${cortes.length}`}
          </div>
        </div>

        <button
          onClick={() => {
            if (aviso) return;
            const indo = modo === 'edicao' ? 'leitura' : 'edicao';
            if (indo === 'leitura') {
              guardar(); setGaveta(null); esquecerPendente();
              // Ler é sem teclado: se ele estava aberto, desce agora.
              if (editorComFoco()) document.activeElement.blur();
            }
            // Ir para a edição NÃO dá foco a nada. O teclado sobe quando a
            // pessoa tocar no texto — antes subia sozinho com este botão.
            setModo(indo);
          }}
          aria-label={modo === 'edicao' ? 'Modo leitura' : 'Modo edição'}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, height: 32, padding: '0 11px',
            borderRadius: 16, cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5,
            opacity: aviso ? 0.4 : 1,
            background: modo === 'edicao' ? 'var(--ember-soft)' : 'var(--paper-2)',
            border: `1px solid ${modo === 'edicao' ? 'var(--ember)' : 'var(--line)'}`,
            color: modo === 'edicao' ? 'var(--ember-2)' : 'var(--ink-3)',
          }}>
          <Icon name={modo === 'edicao' ? 'edit' : 'eye'} size={13}
            color={modo === 'edicao' ? 'var(--ember-2)' : 'var(--ink-3)'} />
          {modo === 'edicao' ? 'Edição' : 'Leitura'}
        </button>

        {/* A LUPA no lugar da lista.
            O ícone de lista abria as folhas como se cada uma fosse uma nota
            solta — fazia sentido quando o caderno era uma pilha de notas, e
            deixou de fazer quando a folha virou página. O que se quer num
            caderno de dez folhas é achar a palavra. */}
        <button
          onClick={() => {
            if (busca.aberta) { fecharBusca(); return; }
            setBusca({ aberta: true, termo: '', achados: [], atual: 0 });
            requestAnimationFrame(() => campoBusca.current?.focus());
          }}
          aria-label="Procurar"
          style={{ ...redondo, ...(busca.aberta ? { background: 'var(--ember-soft)', borderColor: 'var(--ember)' } : null) }}>
          <Icon name="search" size={15} color={busca.aberta ? 'var(--ember-2)' : 'var(--ink-2)'} />
        </button>
      </div>

      {aviso && (
        <div className="mono" style={{
          flex: 'none', margin: '0 14px 8px', padding: '8px 10px', borderRadius: 10,
          background: 'var(--ember-soft)', color: 'var(--ember-2)',
          fontSize: 10, letterSpacing: '0.04em', lineHeight: 1.5,
        }}>{aviso}</div>
      )}

      {recado && (
        <div role="status" style={{
          flex: 'none', margin: '0 14px 8px', padding: '9px 10px 9px 12px', borderRadius: 10,
          background: 'var(--paper-2)', border: '1px solid var(--line-strong)', color: 'var(--ink-2)',
          fontSize: 12, lineHeight: 1.45, display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <span style={{ flex: 1 }}>{recado}</span>
          <button onClick={() => setRecado(null)} aria-label="Fechar recado"
            onMouseDown={(e) => e.preventDefault()}
            style={{ color: 'var(--ink-3)', fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>
      )}

      {/* O LIMITE DO CADERNO, À VISTA ANTES DE CHEGAR. Passando de 90%, avisa;
          no teto, diz com todas as letras que o que vier depois não é salvo —
          o contrário do que acontecia, quando o fim do texto sumia calado. */}
      {teto > 0 && (
        <div role="status" className="mono" style={{
          flex: 'none', margin: '0 14px 8px', padding: '8px 10px', borderRadius: 10,
          background: teto >= 100 ? 'var(--ember-soft)' : 'var(--paper-2)',
          border: '1px solid ' + (teto >= 100 ? 'var(--ember)' : 'var(--line)'),
          color: teto >= 100 ? 'var(--ember-2)' : 'var(--ink-3)',
          fontSize: 10, letterSpacing: '0.04em', lineHeight: 1.5,
        }}>
          {teto >= 100
            ? 'ESTE CADERNO CHEGOU AO LIMITE. O QUE FOR ESCRITO DAQUI EM DIANTE NÃO SERÁ SALVO — CONTINUE NUM CADERNO NOVO.'
            : 'ESTE CADERNO ESTÁ PERTO DO LIMITE (' + teto + '%). VALE COMEÇAR UM CADERNO NOVO PARA O PRÓXIMO TEXTO.'}
        </div>
      )}

      {/* ── a barra de busca ── */}
      {busca.aberta && (
        <div style={{
          flex: 'none', display: 'flex', alignItems: 'center', gap: 8,
          padding: '0 14px 10px',
        }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--paper-2)', border: '1px solid var(--line-strong)',
            borderRadius: 14, padding: '0 12px', height: 38,
          }}>
            <Icon name="search" size={14} color="var(--ink-4)" />
            <input
              ref={campoBusca}
              value={busca.termo}
              onChange={(e) => buscar(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); andarNaBusca(e.shiftKey ? -1 : 1); }
                if (e.key === 'Escape') fecharBusca();
              }}
              placeholder="Procurar no caderno"
              style={{
                flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
                fontFamily: 'inherit', fontSize: 14.5, color: 'var(--ink)',
              }}
            />
            {/* O contador só aparece depois que há o que contar: "0 de 0" numa
                caixa vazia parece defeito. */}
            {busca.termo.trim().length >= 2 && (
              <span className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', flexShrink: 0 }}>
                {busca.achados.length ? `${busca.atual + 1}/${busca.achados.length}` : 'nada'}
              </span>
            )}
          </div>
          <button onClick={() => andarNaBusca(-1)} aria-label="Anterior" disabled={!busca.achados.length}
            style={{ ...redondo, opacity: busca.achados.length ? 1 : 0.35 }}>
            <Icon name="arrow-up" size={14} color="var(--ink-2)" />
          </button>
          <button onClick={() => andarNaBusca(1)} aria-label="Próximo" disabled={!busca.achados.length}
            style={{ ...redondo, opacity: busca.achados.length ? 1 : 0.35 }}>
            <Icon name="arrow-down" size={14} color="var(--ink-2)" />
          </button>
        </div>
      )}

      {/* ── as folhas ── */}
      <div
        ref={palcoRef}
        onWheel={rodaHorizontal}
        onScroll={rolouOPalco}
        style={{
          flex: 1, minHeight: 0, position: 'relative',
          // Vertical liberado também no lado a lado: com o teclado aberto a
          // folha é mais alta que o espaço visível, e sem isso o fim dela
          // ficaria inalcançável.
          overflowY: 'auto',
          overflowX: prefs.layout === 'lado' ? 'auto' : 'hidden',
          display: prefs.layout === 'lado' ? 'flex' : 'block',
          scrollSnapType: prefs.layout === 'lado' ? 'x mandatory' : undefined,
          gap: prefs.layout === 'lado' ? 14 : undefined,
          padding: `4px 16px calc(${modo === 'edicao' ? 64 : 14}px + env(safe-area-inset-bottom, 0px))`,
        }}>

        {/* ── O PAPEL DE MEDIÇÃO ──────────────────────────────────────
            Uma folha invisível do tamanho exato da de verdade, onde o documento
            INTEIRO é desenhado só para ser medido. A conta da página sai daqui,
            e é por isso que ela não precisa tocar no que a pessoa escreveu:
            medir e editar deixaram de ser a mesma operação.

            `aria-hidden` e sem `data-folha` de propósito — nem leitor de tela
            nem teste devem encontrá-la. */}
        <div aria-hidden="true" style={{
          ...folhaBase,
          position: 'absolute', left: 16, right: 16, top: 0,
          visibility: 'hidden', pointerEvents: 'none', zIndex: -1,
          boxShadow: 'none', height: 'auto',
        }}>
          <div ref={medidorRef} data-medidor="1" className={classesFolha} style={{ color: PAPEL.tinta }} />
        </div>

        {carregando ? (
          <div style={{ ...folhaBase, minHeight: paginado ? medida.altura : '100%', opacity: 0.4 }}>{margemDe(false)}</div>
        ) : !paginado ? (
          // FOLHA ÚNICA: um papel só, sem corte nenhum — nem na tela.
          <div style={{ ...folhaBase, minHeight: '100%', marginBottom: folga }}>
            {margemDe(modo === 'edicao')}
            {editor(0)}
          </div>
        ) : (
          cortes.map((_, k) => (
            <div key={`folha-${k}`} style={{
              ...folhaBase,
              flex: prefs.layout === 'lado' ? '0 0 100%' : undefined,
              // MÍNIMO, e não fixo: um parágrafo maior que a página faz o papel
              // crescer em vez de o texto ser cortado.
              minHeight: medida.altura,
              flexShrink: 0,
              scrollSnapAlign: prefs.layout === 'lado' ? 'center' : undefined,
              // A folga da gaveta vai na MARGEM, e não no respiro do palco: o
              // respiro entra na conta da altura da folha, e abrir a gaveta
              // repaginaria o caderno inteiro.
              marginBottom: prefs.layout === 'baixo'
                ? 14 + (k === cortes.length - 1 ? folga : 0)
                : folga,
            }}>
              {margemDe(modo === 'edicao' && k === ativa)}
              {editor(k)}
              <div className="mono" style={{
                position: 'absolute', bottom: 8, right: 14, fontSize: 9, color: PAPEL.fraco,
              }}>{k + 1}</div>
            </div>
          ))
        )}

        {/* Não há botão de "nova folha": ela nasce de escrever até o fim da
            última, como no papel. */}

        {!carregando && outras > 0 && (
          <div className="mono" style={{
            fontSize: 9.5, letterSpacing: '0.08em', color: 'var(--ink-4)',
            textAlign: 'center', marginTop: 12,
          }}>
            {outras} {outras === 1 ? 'NOTA DE VOZ OU FOTO' : 'NOTAS DE VOZ OU FOTO'} NESTE CADERNO
            <br />PROCURE POR ELAS EM TODAS AS NOTAS
          </div>
        )}
      </div>

      {/* ── a gaveta de uma ferramenta ── */}
      {modo === 'edicao' && gaveta && (
        <div
          ref={gavetaRef}
          onPointerDown={lembrarSelecao}
          // NÃO ROUBA O FOCO DO TEXTO — para dedo e para mouse. Ver a barra.
          onMouseDown={(e) => e.preventDefault()}
          style={{
          position: 'fixed', left: 10, right: 10, zIndex: 9002,
          bottom: `calc(${60 + fundo}px + env(safe-area-inset-bottom, 0px))`,
          background: 'var(--paper-2)', border: '1px solid var(--line-strong)',
          borderRadius: 16, padding: 8,
          boxShadow: '0 18px 40px -18px rgba(0,0,0,.6)',
          display: 'flex', flexDirection: 'column', gap: 4,
          // CABE ACIMA DO TECLADO, E DEIXA O TEXTO À VISTA. Com 42vh fixos e o
          // teclado aberto, a gaveta cobria praticamente tudo o que sobrava da
          // tela — inclusive o trecho cuja letra se estava trocando. Agora ela
          // usa no máximo o que sobra menos a barra e uns 150px de texto.
          maxHeight: Math.max(150, Math.min(window.innerHeight * 0.42, window.innerHeight - fundo - 60 - 150)),
          overflowY: 'auto',
        }}>
          {/* Cada nome aparece NA PRÓPRIA LETRA: é a amostra, não o rótulo.
              Escolher fonte por uma lista toda na mesma fonte é escolher no
              escuro. */}
          {gaveta === 'fonte' && FONTES.map((o) => (
            <OpcaoGaveta key={o.key} ativa={marcas.letra === o.key}
              onClick={() => aplicarLetra(o.key)}
              familia={o.familia} rotulo={o.label} dica={o.cara} corpo={17 * o.escala}
              aoTocar={lembrarSelecao} />
          ))}
          {gaveta === 'tamanho' && TAMANHOS.map((o) => (
            <OpcaoGaveta key={o.key} ativa={marcas.corpo === o.key}
              onClick={() => aplicarCorpo(o.key)} corpo={o.px} rotulo={o.label}
              aoTocar={lembrarSelecao} />
          ))}
          {gaveta === 'espaco' && ESPACOS.map((o) => (
            <OpcaoGaveta key={o.key} ativa={marcas.espaco === o.key}
              onClick={() => aplicarEspaco(o.key)} rotulo={o.label} dica={o.dica}
              aoTocar={lembrarSelecao} />
          ))}
          {gaveta === 'folhas' && LAYOUTS.map((o) => (
            <OpcaoGaveta key={o.key} ativa={prefs.layout === o.key}
              onClick={() => trocarLayout(o.key)}
              rotulo={o.label} dica={o.dica} aoTocar={lembrarSelecao} />
          ))}
        </div>
      )}

      {/* ── a barra de ferramentas ──
          Rola para o lado: cabem mais ferramentas do que a largura da tela, e
          empilhar em duas linhas comeria a altura onde a mão escreve. Fica
          acima do teclado porque `fundo` vem do visualViewport. */}
      {modo === 'edicao' && (
        <div
          className="barra-ferramentas"
          // ── A BARRA NÃO ROUBA O FOCO DO TEXTO ──────────────────────
          //
          // `preventDefault` no MOUSEDOWN é o que impede o botão de tirar o
          // foco do editor — e foco tirado é teclado fechado. No toque o
          // navegador sintetiza esse mousedown depois do dedo, e cancelá-lo
          // tem o mesmo efeito: o editor continua ativo, o teclado continua
          // em cima, e o `click` chega ao botão normalmente.
          //
          // Em 30/09 isto foi restrito ao mouse, sob a hipótese de que no dedo
          // cancelar o mousedown matava o clique da gaveta. A hipótese estava
          // errada (a gaveta "não respondia" porque escolher com o cursor
          // parado não mudava nada visível) e a restrição tinha um preço: com o
          // dedo, cada toque na barra fechava o teclado. Volta a valer para
          // todo ponteiro.
          onMouseDown={(e) => e.preventDefault()}
          onPointerDown={(e) => {
            lembrarSelecao();
            if (e.pointerType === 'mouse') pegarBarra(e);
          }}
          // ARRASTAR COM O MOUSE, porque no computador não há dedo.
          //
          // A barra sempre foi `overflow-x: auto`, o que no celular basta: o
          // dedo empurra. No computador, não — arrastar uma área rolável com o
          // botão do mouse não rola coisa nenhuma, e a roda vertical também
          // não anda para o lado. Quem estivesse numa janela estreita
          // simplesmente não alcançava Letra, Tamanho, Entrelinha e Folhas.
          onWheel={(e) => {
            const el = e.currentTarget;
            if (el.scrollWidth <= el.clientWidth) return;
            // A roda comum é vertical; aqui ela vira horizontal. Se o aparelho
            // mandar deltaX (trackpad), respeita o que veio.
            el.scrollLeft += Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
          }}
          // O ARRASTO NÃO PODE VIRAR CLIQUE. Sem isto, puxar a barra para o
          // lado dispararia a ferramenta onde o dedo (ou o ponteiro) parou.
          onClickCapture={(e) => {
            if (!arrastouDeVerdade.current) return;
            arrastouDeVerdade.current = false;
            e.preventDefault();
            e.stopPropagation();
          }}
          ref={barraRef}
          // SÓ REDESENHA QUANDO A RESPOSTA MUDA.
          //
          // `setBarraNoFim` a cada tique de rolagem redesenhava o caderno
          // inteiro dezenas de vezes por segundo — o arrasto engasgava.
          onScroll={(e) => {
            const el = e.currentTarget;
            const fim = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
            setBarraNoFim((antes) => (antes === fim ? antes : fim));
          }}
          style={{
            position: 'fixed', left: 0, right: 0, bottom: fundo, zIndex: 9003,
            ...(barraNoFim ? null : {
              WebkitMaskImage: 'linear-gradient(to right, #000 84%, rgba(0,0,0,0.12) 100%)',
              maskImage: 'linear-gradient(to right, #000 84%, rgba(0,0,0,0.12) 100%)',
            }),
            display: 'flex', alignItems: 'center', gap: 2,
            // Com o teclado fechado, a barra encostava no indicador da casa do
            // iPhone. Com o teclado aberto o recorte de baixo vale 0, entao a
            // mesma conta serve para os dois casos.
            padding: '6px 10px calc(6px + env(safe-area-inset-bottom, 0px))',
            background: 'var(--paper-2)', borderTop: '1px solid var(--line)',
            overflowX: 'auto', overflowY: 'hidden',
            scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
          }}>
          {FERRAMENTAS.map((f, k) => f.divisor ? (
            <span key={`d${k}`} style={{ width: 1, height: 20, background: 'var(--line)', margin: '0 6px', flexShrink: 0 }} />
          ) : (
            <button
              key={f.chave}
              title={f.titulo}
              aria-label={f.titulo}
              onPointerDown={lembrarSelecao}
              onMouseDown={(e) => e.preventDefault()}
              onClick={f.fazer}
              style={{
                flexShrink: 0, minWidth: 38, height: 38, borderRadius: 10,
                border: 'none', cursor: 'pointer', padding: '0 9px',
                // Sem espera de duplo toque e sem o zoom que engole o clique.
                touchAction: 'manipulation',
                background: aceso(f) ? 'var(--ember-soft)' : 'transparent',
                color: aceso(f) ? 'var(--ember-2)' : 'var(--ink-2)',
                fontSize: 15,
                // Desfazer sem nada para desfazer fica apagado — mas tocável:
                // um botão que some e volta faria a barra pular sob o dedo.
                opacity: f.vazio ? 0.35 : 1,
                fontFamily: f.serif ? 'Instrument Serif, Georgia, serif' : 'inherit',
                fontWeight: f.peso || 500,
                fontStyle: f.italico ? 'italic' : 'normal',
                textDecoration: f.sublinhado ? 'underline' : f.riscado ? 'line-through' : 'none',
              }}>{f.rotulo}</button>
          ))}
        </div>
      )}
    </div>
  );
};

function OpcaoGaveta({ ativa, onClick, rotulo, dica, familia, corpo, aoTocar }: any) {
  return (
    <button
      onPointerDown={() => { if (aoTocar) aoTocar(); }}
      // NÃO ROUBA O FOCO DO TEXTO, para dedo e para mouse: o teclado aberto
      // continua aberto enquanto se escolhe a letra. (Ver o comentário da
      // barra de ferramentas: por um dia isto valeu só para o mouse, e com o
      // dedo cada escolha fechava o teclado.)
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={{
        touchAction: 'manipulation',
        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
        borderRadius: 11, cursor: 'pointer', textAlign: 'left', width: '100%',
        background: ativa ? 'var(--ember-soft)' : 'transparent',
        border: `1px solid ${ativa ? 'var(--ember)' : 'transparent'}`,
        fontFamily: familia || 'inherit',
        fontSize: corpo ? Math.min(19, corpo) : 13.5,
        color: ativa ? 'var(--ember-2)' : 'var(--ink)',
        fontWeight: ativa ? 600 : 500,
      }}>
      {rotulo}
      {dica && <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--ink-4)', fontFamily: 'inherit' }}>{dica}</span>}
    </button>
  );
}

const redondo: React.CSSProperties = {
  width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
  background: 'var(--paper-2)', border: '1px solid var(--line)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
